import axios from "axios";
import { withListPagination } from "./utils/parseApiPagination";
import {
  aliasReverseMutationResponse,
  aliasReverseShowResponse,
  translateReverseGirviFormData,
  translateReverseSettlementPayload,
} from "./utils/reverseGirviShape";
import { sanitizePermissionCodes } from "./utils/permissions";

const sanitizeBranchAccessesForErp = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    if (!row || typeof row !== "object") return row;
    if (row.permission_codes === undefined) return row;
    return {
      ...row,
      permission_codes: sanitizePermissionCodes(row.permission_codes),
    };
  });

// Simple ERP auth/permissions storage keys (plain, no encryption)
// These are the canonical keys used across the app.
const ERP_TOKEN_KEY = "api-token";
// Legacy key some builds / sibling apps used — never write; migrate then remove.
const ERP_TOKEN_KEY_LEGACY = "api_token";
const ERP_EXPIRES_KEY = "erpTokenExpiresAt";
const ERP_USER_KEY = "user-details";
const ERP_PERMISSIONS_KEY = "user-permission";
const ERP_BRANCH_DETAILS_KEY = "branch_details";
const ERP_TENANT_NAME_KEY = "tenant_name";
const ERP_TENANT_BRANDING_KEY = "tenant_branding";
const ERP_TENANT_BRANDING_FILES_KEY = "tenant_branding_files";
const TENANT_BRAND_IMAGE_FIELDS = ["logo", "favicon", "sidebar_icon", "signature"];

/** Public-folder stand-ins used until a tenant uploads branding, or if a file is missing. */
export const TENANT_PUBLIC_BRAND_ASSETS = {
  logo: "/assets/images/logos/logo_1.png",
  favicon: "/assets/images/logos/favicon.png",
  sidebar_icon: "/assets/images/logos/sidebaricon.png",
  signature: "/assets/images/logos/signature.png",
};
const ERP_FINANCIAL_YEAR_KEY = "financial_year";
const ERP_ACTIVE_BRANCH_KEY = "active_branch_id";

/** Resolve token; migrate legacy `api_token` → `api-token` once. */
const readErpTokenFromStorage = () => {
  const token = localStorage.getItem(ERP_TOKEN_KEY);
  if (token) {
    // Drop any duplicate legacy key so localStorage shows a single token.
    if (localStorage.getItem(ERP_TOKEN_KEY_LEGACY) != null) {
      localStorage.removeItem(ERP_TOKEN_KEY_LEGACY);
    }
    return token;
  }

  const legacy = localStorage.getItem(ERP_TOKEN_KEY_LEGACY);
  if (legacy) {
    localStorage.setItem(ERP_TOKEN_KEY, legacy);
    localStorage.removeItem(ERP_TOKEN_KEY_LEGACY);
    return legacy;
  }

  return null;
};

// ERP base URL (no trailing slash).
// - access mode uses `/api/...`
// - quote mode uses `/quote/...`
export const getErpBaseUrl = (pinTypeOverride = null) =>
  (() => {
    const currentPinType =
      pinTypeOverride != null
        ? pinTypeOverride
        : typeof window !== "undefined"
          ? localStorage.getItem("currentPinType") || "access"
          : "access";

    const isQuoteMode = String(currentPinType).toLowerCase() === "quote";

    const raw = isQuoteMode
      ? process.env.REACT_APP_QUOTE_API_URL ||
        process.env.REACT_APP_API_URL ||
        process.env.REACT_APP_ERP_BASE_URL ||
        "https://api-syn.vedascodetech.in/quote/"
      : process.env.REACT_APP_API_URL ||
        process.env.REACT_APP_ERP_BASE_URL ||
        "https://api-syn.vedascodetech.in/api/";

    let url = String(raw).trim().replace(/\/+$/, "");

    if (isQuoteMode) {
      // Quote routes are served under `/quote`.
      if (!url.endsWith("/quote") && !url.endsWith("quote")) {
        url = `${url}/quote`;
      }
    } else {
      // Access routes are served under `/api`.
      if (!url.endsWith("/api") && !url.endsWith("api")) {
        url = `${url}/api`;
      }
    }

    return url.replace(/\/+$/, "");
  })();

// Basic in-memory cache (keeps UI responsive between reloads)
let inMemoryAuth = null;
let lastPermsRaw = undefined;
let lastUserRaw = undefined;
let lastTokenRaw = undefined;

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (
      !e.key ||
      e.key === ERP_PERMISSIONS_KEY ||
      e.key === ERP_USER_KEY ||
      e.key === ERP_TOKEN_KEY
    ) {
      inMemoryAuth = null;
      lastPermsRaw = undefined;
      lastUserRaw = undefined;
      lastTokenRaw = undefined;
      dispatchPermissionsUpdated();
    }
  });
}

export const getErpAuthState = () => {
  const token = readErpTokenFromStorage();
  const permsRaw = localStorage.getItem(ERP_PERMISSIONS_KEY);
  const userRaw = localStorage.getItem(ERP_USER_KEY);

  if (
    inMemoryAuth &&
    permsRaw === lastPermsRaw &&
    userRaw === lastUserRaw &&
    token === lastTokenRaw
  ) {
    return inMemoryAuth;
  }

  const expiresAtRaw = localStorage.getItem(ERP_EXPIRES_KEY);

  // expiry can be either:
  // - epoch seconds (10 digits)
  // - epoch milliseconds (13 digits)
  // - an ISO date string
  const expires_at = (() => {
    if (!expiresAtRaw) return null;

    const numeric = Number(expiresAtRaw);
    if (!Number.isNaN(numeric) && numeric) {
      // Heuristic: <= 1e12 is usually seconds, otherwise milliseconds.
      return numeric < 1e12 ? numeric * 1000 : numeric;
    }

    const parsedMs = Date.parse(expiresAtRaw);
    if (!Number.isNaN(parsedMs) && parsedMs) return parsedMs;

    return null;
  })();

  const user = userRaw ? JSON.parse(userRaw) : null;
  const permissions = permsRaw ? JSON.parse(permsRaw) : null;
  const nowMs = Date.now();
  const isTokenExpired =
    !!token &&
    expires_at !== null &&
    typeof expires_at === "number" &&
    nowMs > expires_at;

  if (isTokenExpired) {
    localStorage.removeItem(ERP_TOKEN_KEY);
    localStorage.removeItem(ERP_TOKEN_KEY_LEGACY);
    localStorage.removeItem(ERP_EXPIRES_KEY);
    localStorage.removeItem(ERP_USER_KEY);
    localStorage.removeItem(ERP_PERMISSIONS_KEY);
    localStorage.removeItem(ERP_BRANCH_DETAILS_KEY);
    localStorage.removeItem(ERP_TENANT_NAME_KEY);
    localStorage.removeItem(ERP_TENANT_BRANDING_KEY);
    localStorage.removeItem(ERP_TENANT_BRANDING_FILES_KEY);
    localStorage.removeItem(ERP_ACTIVE_BRANCH_KEY);

    lastTokenRaw = null;
    lastPermsRaw = null;
    lastUserRaw = null;
    inMemoryAuth = {
      token: null,
      expires_at: null,
      user: null,
      permissions: null,
    };
    return inMemoryAuth;
  }

  lastTokenRaw = token;
  lastPermsRaw = permsRaw;
  lastUserRaw = userRaw;
  inMemoryAuth = { token, expires_at, user, permissions };
  return inMemoryAuth;
};

/** Normalize ERP permissions to `{ CODE: 1|0 }` (accepts map or code array). */
const normalizeErpPermissionsMap = (permissions, user) => {
  let map = {};
  if (Array.isArray(permissions)) {
    permissions.forEach((code) => {
      const key = String(code || "")
        .trim()
        .toUpperCase();
      if (key) map[key] = 1;
    });
  } else if (permissions && typeof permissions === "object") {
    map = { ...permissions };
  }

  const userCodes = user?.permission_codes;
  if (Array.isArray(userCodes)) {
    userCodes.forEach((code) => {
      const key = String(code || "")
        .trim()
        .toUpperCase();
      if (key && map[key] === undefined) map[key] = 1;
    });
  }

  return Object.keys(map).length ? map : permissions || null;
};

export const setErpAuthState = ({ token, expires_at, user, permissions }) => {
  const normalizedPermissions = normalizeErpPermissionsMap(permissions, user);
  inMemoryAuth = {
    token: token || null,
    expires_at: expires_at || null,
    user: user || null,
    permissions: normalizedPermissions || null,
  };

  // Single source of truth: only `api-token` (never also write `api_token`).
  localStorage.removeItem(ERP_TOKEN_KEY_LEGACY);
  if (token) {
    localStorage.setItem(ERP_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(ERP_TOKEN_KEY);
  }

  if (expires_at) {
    localStorage.setItem(ERP_EXPIRES_KEY, String(expires_at));
  } else {
    localStorage.removeItem(ERP_EXPIRES_KEY);
  }

  if (user) {
    localStorage.setItem(ERP_USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(ERP_USER_KEY);
  }

  if (normalizedPermissions) {
    localStorage.setItem(
      ERP_PERMISSIONS_KEY,
      JSON.stringify(normalizedPermissions),
    );
  } else {
    localStorage.removeItem(ERP_PERMISSIONS_KEY);
  }

  lastTokenRaw = undefined;
  lastPermsRaw = undefined;
  lastUserRaw = undefined;

  try {
    window.dispatchEvent(new CustomEvent("erpAuthUpdated"));
    window.dispatchEvent(new CustomEvent("permissionsUpdated"));
  } catch {
    // ignore
  }
};

export const clearErpAuthState = () => {
  inMemoryAuth = null;
  lastTokenRaw = null;
  lastPermsRaw = null;
  lastUserRaw = null;
  localStorage.removeItem(ERP_TOKEN_KEY);
  localStorage.removeItem(ERP_TOKEN_KEY_LEGACY);
  localStorage.removeItem(ERP_EXPIRES_KEY);
  localStorage.removeItem(ERP_USER_KEY);
  localStorage.removeItem(ERP_PERMISSIONS_KEY);
  localStorage.removeItem(ERP_BRANCH_DETAILS_KEY);
  localStorage.removeItem(ERP_TENANT_NAME_KEY);
  localStorage.removeItem(ERP_TENANT_BRANDING_KEY);
  localStorage.removeItem(ERP_TENANT_BRANDING_FILES_KEY);
  localStorage.removeItem(ERP_ACTIVE_BRANCH_KEY);
  clearFiscalYearsListCache();
  try {
    window.dispatchEvent(new CustomEvent("erpAuthUpdated"));
    window.dispatchEvent(new CustomEvent("permissionsUpdated"));
  } catch {
    // ignore
  }
};

/** Branches returned from OTP verify / auth — stored as `branch_details`. */
export const getErpBranchDetails = () => {
  try {
    const raw = localStorage.getItem(ERP_BRANCH_DETAILS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const setErpBranchDetails = (branches) => {
  if (Array.isArray(branches)) {
    localStorage.setItem(ERP_BRANCH_DETAILS_KEY, JSON.stringify(branches));
  } else {
    localStorage.removeItem(ERP_BRANCH_DETAILS_KEY);
  }
};

function normalizeBranchList(rawBranches) {
  if (!Array.isArray(rawBranches)) return [];
  return rawBranches
    .map((b) => {
      if (!b || typeof b !== "object") return null;
      const id = Number(b.id ?? b.branch_id);
      if (!Number.isFinite(id) || id <= 0) return null;
      return { ...b, id };
    })
    .filter(Boolean);
}

function resolveActiveBranchIdFromPayload({
  active_branch_id,
  user,
  branches,
}) {
  const list = normalizeBranchList(branches);
  try {
    const stored = localStorage.getItem(ERP_ACTIVE_BRANCH_KEY);
    if (stored != null && String(stored).trim() !== "") {
      const storedId = Number(stored);
      if (Number.isFinite(storedId) && storedId > 0) {
        if (!list.length || list.some((b) => Number(b.id) === storedId)) {
          return storedId;
        }
      }
    }
  } catch {
    // ignore
  }

  const candidates = [
    active_branch_id,
    user?.active_branch_id,
    user?.branch_id,
    user?.branchId,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const def = list.find((b) => b.is_default === true || b.is_default === 1);
  return def ? Number(def.id) : list[0] ? Number(list[0].id) : null;
}

function findBranchById(branches, branchId) {
  const id = Number(branchId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return (
    normalizeBranchList(branches).find((b) => Number(b.id) === id) || null
  );
}

function dispatchPermissionsUpdated() {
  try {
    window.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: {} }));
  } catch {
    // ignore
  }
}

function dispatchBranchChanged(branchId) {
  try {
    window.dispatchEvent(
      new CustomEvent("branchChanged", { detail: { branchId } }),
    );
    window.dispatchEvent(
      new CustomEvent("branchUpdated", { detail: { branchId } }),
    );
    window.dispatchEvent(
      new CustomEvent("erpAuthUpdated", { detail: { branchId } }),
    );
  } catch {
    // ignore
  }
}

/** Active branch id for session (localStorage + user.active_branch_id). */
export const getActiveBranchId = () => {
  try {
    const stored = localStorage.getItem(ERP_ACTIVE_BRANCH_KEY);
    if (stored != null && String(stored).trim() !== "") {
      const n = Number(stored);
      if (Number.isFinite(n) && n > 0) return n;
    }
  } catch {
    // ignore
  }
  const user = getErpAuthState()?.user;
  const fromUser = Number(
    user?.active_branch_id ?? user?.branch_id ?? user?.branchId,
  );
  return Number.isFinite(fromUser) && fromUser > 0 ? fromUser : null;
};

export const getActiveBranch = () => {
  const id = getActiveBranchId();
  if (id == null) return null;
  return findBranchById(getErpBranchDetails(), id);
};

/**
 * Switch active branch: update active_branch_id, swap permissions/roles from
 * that branch entry, keep branch_details in sync, notify menus.
 */
export const setActiveBranchId = (branchId) => {
  const id = Number(branchId);
  if (!Number.isFinite(id) || id <= 0) return false;

  const branches = getErpBranchDetails();
  const branch = findBranchById(branches, id);
  if (!branch && branches.length > 0) return false;

  localStorage.setItem(ERP_ACTIVE_BRANCH_KEY, String(id));

  const auth = getErpAuthState();
  const prevUser = auth.user && typeof auth.user === "object" ? auth.user : {};
  const nextUser = {
    ...prevUser,
    active_branch_id: id,
    branch_id: id,
  };

  if (branch) {
    if (Array.isArray(branch.roles)) nextUser.roles = branch.roles;
    if (Array.isArray(branch.role_ids)) nextUser.role_ids = branch.role_ids;
    if (Array.isArray(branch.permission_codes)) {
      nextUser.permission_codes = branch.permission_codes;
    }
    if (branch.name != null) nextUser.branch_name = branch.name;
  }

  const nextPermissions =
    branch?.permissions &&
    typeof branch.permissions === "object" &&
    !Array.isArray(branch.permissions)
      ? branch.permissions
      : auth.permissions;

  setErpAuthState({
    token: auth.token,
    expires_at: auth.expires_at,
    user: nextUser,
    permissions: nextPermissions || null,
  });

  dispatchPermissionsUpdated();
  dispatchBranchChanged(id);
  return true;
};

/**
 * Persist login /auth/me session payload including branches + active_branch_id.
 */
export function applyAuthSessionPayload(data, { keepToken } = {}) {
  if (!data || typeof data !== "object") return;

  const existing = getErpAuthState();
  const token =
    keepToken !== undefined && keepToken !== null
      ? keepToken
      : data.token ?? existing.token;

  const branches = normalizeBranchList(
    data.branches ??
      data.branch_details ??
      data.user?.branches ??
      data.user?.branch_details ??
      data.branch ??
      null,
  );
  if (branches.length > 0) {
    setErpBranchDetails(branches);
  }

  const activeId = resolveActiveBranchIdFromPayload({
    active_branch_id: data.active_branch_id,
    user: data.user,
    branches: branches.length ? branches : getErpBranchDetails(),
  });

  if (activeId != null) {
    localStorage.setItem(ERP_ACTIVE_BRANCH_KEY, String(activeId));
  }

  const activeBranch = findBranchById(
    branches.length ? branches : getErpBranchDetails(),
    activeId,
  );

  let user =
    data.user && typeof data.user === "object" ? { ...data.user } : existing.user;
  if (user && typeof user === "object") {
    if (activeId != null) {
      user.active_branch_id = activeId;
      user.branch_id = activeId;
    }
    if (activeBranch) {
      if (Array.isArray(activeBranch.roles) && !user.roles) {
        user.roles = activeBranch.roles;
      }
      if (Array.isArray(activeBranch.role_ids) && !user.role_ids) {
        user.role_ids = activeBranch.role_ids;
      }
    }
  }

  let permissions = data.permissions;
  if (
    (!permissions || typeof permissions !== "object") &&
    activeBranch?.permissions &&
    typeof activeBranch.permissions === "object"
  ) {
    permissions = activeBranch.permissions;
  }

  setErpAuthState({
    token,
    expires_at: data.expires_at ?? existing.expires_at,
    user,
    permissions: permissions || null,
  });

  const tenantFromBranches = (branches.length
    ? branches
    : getErpBranchDetails()
  ).find((b) => b?.tenant_name != null && String(b.tenant_name).trim() !== "")
    ?.tenant_name;
  const tenantName =
    data.tenant_name ??
    data.tenantName ??
    data.user?.tenant_name ??
    data.user?.tenantName ??
    tenantFromBranches ??
    null;
  if (tenantName) {
    setErpTenantName(tenantName);
  }

  // Store per-tenant branding returned by login / /auth/me.
  const tenantBranding = data.tenant ?? data.user?.tenant ?? null;
  if (tenantBranding && typeof tenantBranding === "object") {
    setErpTenantBranding(tenantBranding);
  }
}

/**
 * Tenant name from `branch_details[].tenant_name`, then OTP-stored `tenant_name`.
 */
export const getErpTenantName = () => {
  try {
    const branches = getErpBranchDetails();
    for (const branch of branches) {
      const name = branch?.tenant_name;
      if (name != null && String(name).trim() !== "") {
        return String(name).trim();
      }
    }
    const stored = localStorage.getItem(ERP_TENANT_NAME_KEY);
    if (stored != null && String(stored).trim() !== "") {
      return String(stored).trim();
    }
    return "";
  } catch {
    return "";
  }
};

/**
 * Returns the API origin (scheme + host, no path) used to prefix tenant image paths.
 * e.g. "https://api-syn.vedascodetech.in"
 */
export const getErpApiOrigin = () => {
  try {
    const base = getErpBaseUrl("access");
    const url = new URL(base.startsWith("http") ? base : `https://${base}`);
    return url.origin;
  } catch {
    return "";
  }
};

/**
 * Prefix a tenant image path with the API origin so it can be used as an img src.
 * Returns null if path is null/empty.
 */
export const getTenantImageUrl = (path) => {
  if (!path || typeof path !== "string" || !path.trim()) return null;
  const trimmed = path.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const origin = getErpApiOrigin();
  return `${origin}${trimmed.startsWith("/") ? trimmed : `/${trimmed}`}`;
};

const readTenantBrandingFiles = () => {
  try {
    const raw = localStorage.getItem(ERP_TENANT_BRANDING_FILES_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Tenant branding image for screens and PDFs.
 * Prefers the copy saved in localStorage after verify_otp, then the API URL.
 * Returns null when this tenant has not uploaded that file.
 */
export const getTenantBrandImageSrc = (field) => {
  const branding = getErpTenantBranding();
  const raw = branding?.[field];
  const path = raw == null ? "" : String(raw).trim();
  if (!path) return null;

  const cached = readTenantBrandingFiles()?.[field];
  if (
    cached?.path === path &&
    typeof cached.dataUrl === "string" &&
    cached.dataUrl.startsWith("data:image/")
  ) {
    return cached.dataUrl;
  }

  return getTenantImageUrl(path);
};

/** Tenant image when uploaded, otherwise the matching file from `public/assets/images/logos`. */
export const getTenantBrandImageSrcWithFallback = (field) =>
  getTenantBrandImageSrc(field) || TENANT_PUBLIC_BRAND_ASSETS[field] || null;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Failed to read image"));
    reader.readAsDataURL(blob);
  });

let tenantBrandCacheSeq = 0;

/**
 * Download tenant logo / favicon / sidebar icon / signature and keep them in localStorage
 * so the app and PDFs do not depend on the public-folder files.
 */
export const cacheTenantBrandingImages = async (tenant) => {
  const seq = ++tenantBrandCacheSeq;
  if (typeof window === "undefined") return;

  if (!tenant || typeof tenant !== "object") {
    try {
      localStorage.removeItem(ERP_TENANT_BRANDING_FILES_KEY);
    } catch {
      // ignore
    }
    return;
  }

  const existing = readTenantBrandingFiles() || {};
  const next = {};

  await Promise.all(
    TENANT_BRAND_IMAGE_FIELDS.map(async (field) => {
      const path = tenant[field] == null ? "" : String(tenant[field]).trim();
      if (!path) return;

      const prev = existing[field];
      if (
        prev?.path === path &&
        typeof prev.dataUrl === "string" &&
        prev.dataUrl.startsWith("data:image/")
      ) {
        next[field] = prev;
        return;
      }

      try {
        const url = getTenantImageUrl(path);
        if (!url) return;
        const res = await fetch(url, { credentials: "omit", cache: "force-cache" });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!blob || blob.size === 0 || blob.size > 1_500_000) return;
        if (blob.type && !blob.type.startsWith("image/")) return;
        const dataUrl = await blobToDataUrl(blob);
        if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) return;
        next[field] = { path, dataUrl };
      } catch {
        // Keep using the remote URL until a later login can cache the file.
      }
    }),
  );

  if (seq !== tenantBrandCacheSeq) return;

  const previousSerialized = JSON.stringify(
    TENANT_BRAND_IMAGE_FIELDS.reduce((acc, field) => {
      if (existing[field]) acc[field] = existing[field];
      return acc;
    }, {}),
  );
  const nextSerialized = JSON.stringify(next);
  if (previousSerialized === nextSerialized) return;

  try {
    localStorage.setItem(ERP_TENANT_BRANDING_FILES_KEY, nextSerialized);
  } catch {
    return;
  }

  try {
    window.dispatchEvent(
      new CustomEvent("erpTenantBrandingUpdated", {
        detail: { tenant: getErpTenantBranding() },
      }),
    );
  } catch {
    // ignore
  }
};

/**
 * Stored tenant branding object: { id, name, logo, favicon, sidebar_icon, signature }
 * Image values are paths like /uploads/tenant_branding/{id}/logo_...png or null.
 */
export const getErpTenantBranding = () => {
  try {
    const raw = localStorage.getItem(ERP_TENANT_BRANDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setErpTenantBranding = (tenant) => {
  if (tenant && typeof tenant === "object") {
    localStorage.setItem(ERP_TENANT_BRANDING_KEY, JSON.stringify(tenant));
    try {
      window.dispatchEvent(new CustomEvent("erpTenantBrandingUpdated", { detail: { tenant } }));
    } catch {
      // ignore
    }
    void cacheTenantBrandingImages(tenant);
  } else {
    localStorage.removeItem(ERP_TENANT_BRANDING_KEY);
    try {
      localStorage.removeItem(ERP_TENANT_BRANDING_FILES_KEY);
    } catch {
      // ignore
    }
    try {
      window.dispatchEvent(
        new CustomEvent("erpTenantBrandingUpdated", { detail: { tenant: null } }),
      );
    } catch {
      // ignore
    }
  }
};

export const setErpTenantName = (name) => {
  const trimmed = name == null ? "" : String(name).trim();
  if (trimmed) {
    localStorage.setItem(ERP_TENANT_NAME_KEY, trimmed);
  } else {
    localStorage.removeItem(ERP_TENANT_NAME_KEY);
  }
};

export const getErpToken = () => getErpAuthState().token || null;

/** Kiosk / walk-in form token — env service account, else logged-in session token. */
export const getKioskErpToken = () => {
  const kiosk = process.env.REACT_APP_KIOSK_TOKEN;
  if (kiosk != null && String(kiosk).trim() !== "") {
    return String(kiosk).trim();
  }
  return getErpToken();
};

export const getErpPermissions = () =>
  getErpAuthState().permissions || Object.create(null);

export const getFinancialYear = () => {
  const value = localStorage.getItem(ERP_FINANCIAL_YEAR_KEY);
  const trimmed = value == null ? "" : String(value).trim();
  return trimmed || null;
};

export const setFinancialYear = (value) => {
  const trimmed = value == null ? "" : String(value).trim();
  if (!trimmed) {
    localStorage.removeItem(ERP_FINANCIAL_YEAR_KEY);
    return null;
  }
  localStorage.setItem(ERP_FINANCIAL_YEAR_KEY, trimmed);
  try {
    window.dispatchEvent(
      new CustomEvent("financialYearUpdated", { detail: { value: trimmed } }),
    );
  } catch {
    // ignore
  }
  return trimmed;
};

/** Normalize `/fiscal_years` payload to a clean string list. */
export const normalizeFiscalYearsList = (response) => {
  const list = Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
      ? response.data
      : [];
  return list
    .map((year) => {
      if (year == null) return "";
      if (typeof year === "string" || typeof year === "number") {
        return String(year).trim();
      }
      if (typeof year === "object") {
        return String(
          year.financial_year ||
            year.year ||
            year.value ||
            year.label ||
            year.name ||
            "",
        ).trim();
      }
      return String(year).trim();
    })
    .filter(Boolean);
};

/** Indian FY label for a date (April–March), e.g. Oct 2026 → "2026-2027". */
export const getIndianCalendarFinancialYear = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date();
  const month = d.getMonth(); // 0-11
  const year = d.getFullYear();
  if (month >= 3) return `${year}-${year + 1}`;
  return `${year - 1}-${year}`;
};

const fiscalYearStartNumber = (value) => {
  const start = String(value || "").trim().split("-")[0];
  const n = Number.parseInt(start, 10);
  return Number.isFinite(n) ? n : 0;
};

/** Resolve which list entry is the active/current FY. */
export const resolveCurrentFinancialYearFromList = (years) => {
  const list = Array.isArray(years) ? years.filter(Boolean) : [];
  if (!list.length) return null;
  const calendarFy = getIndianCalendarFinancialYear();
  if (list.includes(calendarFy)) return calendarFy;
  return list.reduce((best, year) =>
    fiscalYearStartNumber(year) >= fiscalYearStartNumber(best) ? year : best,
  );
};

/**
 * Active/current financial year from the last `/fiscal_years` response.
 */
let currentFinancialYearCache = null;

export const getCurrentFinancialYear = () => currentFinancialYearCache;

/** True when selected session FY matches the active/current FY. */
export const isCurrentFinancialYear = (selected = getFinancialYear()) => {
  const current = getCurrentFinancialYear();
  const sel = selected == null ? "" : String(selected).trim();
  if (!current) return true;
  if (!sel) return true;
  return sel === String(current).trim();
};

// Reusable authorized ERP client using plain JSON + Bearer
const axiosErp = axios.create();

/** In-flight dedupe for identical requests (dev Strict Mode double-mount, double-click, etc.) */
const pendingErpInFlight = new Map();

export const erpFetch = async (
  path,
  {
    method = "GET",
    body,
    idempotencyKey,
    /**
     * When true, concurrent calls with the same method + URL + JSON body share one HTTP request.
     * Use only for idempotent reads/list calls — not for creates/updates.
     */
    dedupeInFlight = false,
    /** When true, do not append `financial_year` from localStorage to JSON bodies. */
    skipFinancialYear = false,
    /** Override Bearer token (e.g. kiosk service account on /customerform). */
    tokenOverride = null,
    /** Override route scope: "quote" -> /quote/... or "access" -> /api/... */
    pinTypeOverride = null,
  } = {},
) => {
  const baseUrl = getErpBaseUrl(pinTypeOverride);
  const url = `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
  const token = tokenOverride || getErpToken();

  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (idempotencyKey != null && String(idempotencyKey).trim() !== "") {
    headers["Idempotency-Key"] = String(idempotencyKey).trim();
  }

  // Active branch context for RBAC (skip WhatsApp + daily price — tenant-wide).
  const pathNorm = String(path || "").toLowerCase();
  const skipBranchHeader =
    pathNorm.includes("whatsapp") ||
    pathNorm.includes("daily_price") ||
    pathNorm.includes("daily-price");
  if (token && !skipBranchHeader) {
    const activeBranchId = getActiveBranchId();
    if (activeBranchId != null) {
      headers["X-Branch-Id"] = String(activeBranchId);
    }
  }

  let data;
  if (body !== undefined && body !== null) {
    headers["Content-Type"] = "application/json";
    data = body;
    const financialYear = getFinancialYear();
    if (
      !skipFinancialYear &&
      financialYear &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      data.financial_year === undefined
    ) {
      data = { ...data, financial_year: financialYear };
    }
  }

  const methodUpper = String(method).toUpperCase();

  // Dev-only: quickly verify whether requests are hitting /api or /quote.
  if (
    process.env.NODE_ENV !== "production" &&
    methodUpper !== "GET" &&
    typeof window !== "undefined"
  ) {
    const pinType = localStorage.getItem("currentPinType") || "access";
    // console.debug("[erpFetch submit]", {
    //   method: methodUpper,
    //   pinType,
    //   baseUrl,
    //   url,
    // });
  }

  const dedupeKey =
    dedupeInFlight === true
      ? `${methodUpper}:${url}:${data === undefined || data === null ? "" : JSON.stringify(data)}`
      : null;

  if (dedupeKey && pendingErpInFlight.has(dedupeKey)) {
    return pendingErpInFlight.get(dedupeKey);
  }

  // Debug logging for auth + payload (helps verify token/JSON shape)
  // if (
  //   process.env.NODE_ENV !== "production" &&
  //   (path.includes("daily_price") || path.includes("bill_type"))
  // ) {
  //   console.log("[ERP FETCH]", {
  //     url,
  //     method,
  //     hasAuthHeader: !!headers.Authorization,
  //     authorizationSample: headers.Authorization
  //       ? `${headers.Authorization.slice(0, 20)}...`
  //       : null,
  //     body: data,
  //   });
  // }

  const requestPromise = (async () => {
    try {
      const response = await axiosErp.request({
        url,
        method,
        headers,
        data,
      });

      return response.data;
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "";
      const messageText = String(message).toLowerCase();

      // 401 = unauthenticated. 403 is often RBAC — only clear session when the message looks like an auth/token failure.
      const tokenLikeMessage =
        messageText.includes("token not found") ||
        messageText.includes("invalid token") ||
        messageText.includes("jwt expired") ||
        messageText.includes("jwt malformed");
      const isTokenNotFound =
        status === 401 || (status === 403 && tokenLikeMessage);

      if (isTokenNotFound) {
        clearErpAuthState();
        try {
          window.dispatchEvent(new Event("erpAuthInvalid"));
        } catch {
          // ignore
        }
        try {
          window.dispatchEvent(new Event("permissionsUpdated"));
        } catch {
          // ignore
        }
      }

      throw err;
    } finally {
      if (dedupeKey) {
        pendingErpInFlight.delete(dedupeKey);
      }
    }
  })();

  if (dedupeKey) {
    pendingErpInFlight.set(dedupeKey, requestPromise);
  }

  return requestPromise;
};

/** Success envelope used across modular ERP POST endpoints */
export const isErpApiSuccess = (res) =>
  res &&
  (res.success === true ||
    String(res.status || "").toLowerCase() === "success" ||
    res.message === "radha");

/**
 * Prefer API `message`, then `error`; map 403 to a clear RBAC hint.
 */
export const getErpRequestErrorMessage = (
  err,
  fallback = "Request failed.",
) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const msg =
    (typeof data?.message === "string" && data.message.trim()) ||
    (typeof data?.error === "string" && data.error.trim()) ||
    null;
  if (msg) return msg;
  if (status === 403) {
    return "You do not have permission for this action.";
  }
  return fallback;
};

/** For money-related ERP POSTs (cancel refunds, karigar conversion, etc.) */
export const generateIdempotencyKey = () => {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
};

// ---------- Auth endpoints ----------

const FISCAL_YEARS_LIST_DEDUPE_MS = 2000;
let fiscalYearsListInFlight = null;
let fiscalYearsListCache = null;
let fiscalYearsListCacheAt = 0;

export function clearFiscalYearsListCache() {
  fiscalYearsListCache = null;
  fiscalYearsListCacheAt = 0;
  fiscalYearsListInFlight = null;
  currentFinancialYearCache = null;
}

const rememberCurrentFinancialYearFromList = (response) => {
  const years = normalizeFiscalYearsList(response);
  currentFinancialYearCache = resolveCurrentFinancialYearFromList(years);
  return years;
};

/** Shared list for Navbar + footer; Strict Mode / dual mounts share one HTTP call. */
export const listFiscalYears = ({ force = false } = {}) => {
  const now = Date.now();

  if (!force) {
    if (
      fiscalYearsListCache &&
      now - fiscalYearsListCacheAt < FISCAL_YEARS_LIST_DEDUPE_MS
    ) {
      rememberCurrentFinancialYearFromList(fiscalYearsListCache);
      return Promise.resolve(fiscalYearsListCache);
    }
    if (fiscalYearsListInFlight) return fiscalYearsListInFlight;
  } else {
    clearFiscalYearsListCache();
  }

  fiscalYearsListInFlight = erpFetch("/fiscal_years", {
    method: "POST",
    dedupeInFlight: true,
  })
    .then((res) => {
      fiscalYearsListCache = res;
      fiscalYearsListCacheAt = Date.now();
      rememberCurrentFinancialYearFromList(res);
      return res;
    })
    .finally(() => {
      fiscalYearsListInFlight = null;
    });

  return fiscalYearsListInFlight;
};

export const requestErpOtp = ({ tenantId, phone, type } = {}) =>
  erpFetch("/auth/request_otp", {
    method: "POST",
    body: {
      tenant_id: tenantId,
      phone,
      ...(type ? { type } : null),
    },
  });

export const verifyErpOtp = async ({
  tenantId,
  phone,
  otp,
  persistSession = true,
} = {}) => {
  const result = await erpFetch("/auth/verify_otp", {
    method: "POST",
    body: {
      tenant_id: tenantId,
      phone,
      otp,
    },
  });

  if (persistSession && result?.success && result.data) {
    applyAuthSessionPayload(result.data);
  }

  return result;
};

export const fetchErpMe = async () => {
  const result = await erpFetch("/auth/me", {
    method: "GET",
  });

  if (result?.success && result.data) {
    const { token } = getErpAuthState();
    applyAuthSessionPayload(result.data, { keepToken: token });
    dispatchPermissionsUpdated();
  }

  return result;
};

/** Coalesce `/auth/me` refresh when many components each have their own socket (same event N times). */
let debouncedFetchErpMeTimer = null;
function scheduleDebouncedFetchErpMe() {
  if (typeof window === "undefined") {
    void fetchErpMe().catch(() => {});
    return;
  }
  if (debouncedFetchErpMeTimer != null) {
    window.clearTimeout(debouncedFetchErpMeTimer);
  }
  debouncedFetchErpMeTimer = window.setTimeout(() => {
    debouncedFetchErpMeTimer = null;
    void fetchErpMe().catch(() => {});
  }, 200);
}

/**
 * Apply admin / permission socket payloads to the active ERP session when they target
 * the logged-in user. Handles `{ user }` (e.g. `admin_user_updated`) and `{ employee }`
 * (`employee_permission_updated`). Merges profile + optional permission map, then always
 * schedules a debounced {@link fetchErpMe} so RBAC matches the server (authoritative).
 *
 * @param {object} socketData e.g. `{ user: {...} }` or `{ employee: {...} }`
 * @returns {boolean} true when the event applied to the current ERP session user
 */
export function applyRealtimeSessionFromAdminUserSocket(socketData) {
  const payload = socketData?.user ?? socketData?.employee;
  if (!payload || typeof payload !== "object") return false;

  const auth = getErpAuthState();
  const { token, expires_at, user: authUser, permissions: authPerms } = auth;
  if (!token || !authUser) return false;

  const authNumericId = Number(
    authUser.id ?? authUser.ua_id ?? authUser.user_id,
  );
  const payloadNumericId = Number(
    payload.id ?? payload.ua_id ?? payload.user_id,
  );
  if (!Number.isFinite(authNumericId) || !Number.isFinite(payloadNumericId)) {
    return false;
  }
  if (authNumericId !== payloadNumericId) return false;

  const mergedUser = { ...authUser };

  ["name", "phone", "email", "tenant_id", "branch_id"].forEach((key) => {
    if (payload[key] !== undefined) mergedUser[key] = payload[key];
  });

  if (payload.status !== undefined && payload.status !== null) {
    const s = parseInt(String(payload.status), 10);
    if (!Number.isNaN(s)) mergedUser.status = s;
  }

  if (Array.isArray(payload.roles)) {
    mergedUser.roles = payload.roles;
  } else if (payload.role !== undefined && payload.role !== null) {
    mergedUser.roles = [payload.role];
  }

  const hasPayloadPerms =
    payload.permissions &&
    typeof payload.permissions === "object" &&
    !Array.isArray(payload.permissions) &&
    Object.keys(payload.permissions).length > 0;

  let nextPermissions =
    authPerms && typeof authPerms === "object" && !Array.isArray(authPerms)
      ? { ...authPerms }
      : {};
  if (hasPayloadPerms) {
    nextPermissions = { ...nextPermissions, ...payload.permissions };
  }

  setErpAuthState({
    token,
    expires_at,
    user: mergedUser,
    permissions: nextPermissions,
  });

  try {
    window.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: {} }));
  } catch {
    // ignore
  }

  scheduleDebouncedFetchErpMe();

  return true;
}

// ---------- Daily Price endpoints ----------

export const createDailyPrice = ({
  gold24,
  gold22,
  gold18,
  gold9,
  silver,
  gold18Rose,
  gold9Rose,
}) =>
  erpFetch("/daily_price", {
    method: "POST",
    body: {
      type: "create",
      "24_carat_gold": gold24,
      "22_carat_gold": gold22,
      "18_carat_gold": gold18,
      "9_carat_gold": gold9,
      rose_gold_18: gold18Rose,
      rose_gold_9: gold9Rose,
      silver,
    },
  });

export const listDailyPrices = () =>
  erpFetch("/daily_price", {
    method: "POST",
    body: {
      type: "show",
    },
  });

export const getLastDailyPrice = () =>
  erpFetch("daily_price", {
    method: "POST",
    body: {
      type: "last_entry",
    },
  });

// ---------- Bill Type endpoints ----------

export const createBillType = ({ name, gst_applicable, gst_percent }) =>
  erpFetch("/bill_type", {
    method: "POST",
    body: {
      type: "create",
      name,
      gst_applicable,
      gst_percent,
    },
  });

export const listBillTypes = () =>
  erpFetch("/bill_type", {
    method: "POST",
    body: {
      type: "list",
    },
    dedupeInFlight: true,
  });

// Full update for a bill type (name + GST + status)
export const updateBillTypeStatus = ({
  id,
  name,
  gst_applicable,
  gst_percent,
  status,
}) =>
  erpFetch("/bill_type", {
    method: "POST",
    body: {
      type: "update",
      id,
      name,
      gst_applicable,
      status,
      gst_percent,
    },
  });

// ---------- Bank Account endpoints ----------

export const createBankAccount = ({
  branch_id,
  bank_name,
  holder_name,
  account_type,
  ifsc_code,
  account_number,
  opening_balance,
  status,
} = {}) =>
  erpFetch("/bank_account", {
    method: "POST",
    body: {
      type: "create",
      branch_id,
      bank_name,
      holder_name,
      ifsc_code,
      account_number,
      opening_balance,
      ...(account_type !== undefined && account_type !== null && account_type !== ""
        ? { account_type }
        : {}),
      ...(status !== undefined && status !== null
        ? { status: Number(status) }
        : {}),
    },
  });

export const listBankAccounts = ({ branch_id } = {}) =>
  erpFetch("/bank_account", {
    method: "POST",
    body: branch_id
      ? {
          type: "list",
          branch_id,
        }
      : {
          type: "list",
        },
    dedupeInFlight: true,
  });

export const updateBankAccountStatus = ({
  id,
  branch_id,
  bank_name,
  holder_name,
  account_type,
  ifsc_code,
  account_number,
  opening_balance,
  use_for_invoice,
  use_for_pos,
  status,
}) =>
  erpFetch("/bank_account", {
    method: "POST",
    body: {
      type: "update",
      id,
      branch_id,
      bank_name,
      holder_name,
      ifsc_code,
      account_number,
      opening_balance,
      use_for_invoice,
      use_for_pos,
      status,
      ...(account_type !== undefined && account_type !== null && account_type !== ""
        ? { account_type }
        : {}),
    },
  });

// ---------- Supplier endpoints (unified karigar/supplier) ----------

export const createSupplier = ({
  name,
  company_name,
  contact_number,
  email,
  address,
  city,
  gst_number,
  pan_card,
  short_code,
  primary_supplier,
  gold_balance_gm,
  silver_balance_gm,
  balance,
} = {}) =>
  erpFetch("/supplier", {
    method: "POST",
    body: {
      type: "create",
      name,
      company_name,
      contact_number,
      email,
      address,
      city,
      gst_number,
      ...(pan_card !== undefined &&
      pan_card !== null &&
      String(pan_card).trim() !== ""
        ? { pan_card: String(pan_card).trim() }
        : null),
      short_code,
      primary_supplier,
      ...(gold_balance_gm !== undefined ? { gold_balance_gm } : {}),
      ...(silver_balance_gm !== undefined ? { silver_balance_gm } : {}),
      ...(balance !== undefined ? { balance } : {}),
    },
  });

export const updateSupplier = ({
  id,
  name,
  company_name,
  contact_number,
  email,
  address,
  city,
  gst_number,
  pan_card,
  short_code,
  status,
  primary_supplier,
  gold_balance_gm,
  silver_balance_gm,
  balance,
} = {}) =>
  erpFetch("/supplier", {
    method: "POST",
    body: {
      type: "update",
      id,
      ...(name !== undefined ? { name } : null),
      ...(company_name !== undefined ? { company_name } : null),
      ...(contact_number !== undefined ? { contact_number } : null),
      ...(email !== undefined ? { email } : null),
      ...(address !== undefined ? { address } : null),
      ...(city !== undefined ? { city } : null),
      ...(gst_number !== undefined ? { gst_number } : null),
      ...(pan_card !== undefined
        ? { pan_card: String(pan_card ?? "").trim() }
        : null),
      ...(short_code !== undefined ? { short_code } : null),
      ...(status !== undefined ? { status } : null),
      ...(primary_supplier !== undefined ? { primary_supplier } : null),
      ...(gold_balance_gm !== undefined ? { gold_balance_gm } : {}),
      ...(silver_balance_gm !== undefined ? { silver_balance_gm } : {}),
      ...(balance !== undefined ? { balance } : {}),
    },
  });

export const listSuppliers = ({ search, status, page, limit, pagination } = {}) =>
  erpFetch("/supplier", {
    method: "POST",
    body: withListPagination(
      {
        type: "list",
        ...(search !== undefined ? { search } : null),
        ...(status !== undefined ? { status } : null),
      },
      { page, limit, pagination },
    ),
    dedupeInFlight: true,
  });

export const getSupplier = ({ id, branch_id } = {}) =>
  erpFetch("/supplier", {
    method: "POST",
    body: {
      type: "get",
      id,
      ...(branch_id !== undefined && branch_id !== null ? { branch_id } : null),
    },
  });

// ---------- Customer Users endpoints ----------

export const listCustomerUsers = ({
  search = "",
  page,
  limit,
  pagination,
} = {}) =>
  erpFetch("/customer_users", {
    method: "POST",
    body: withListPagination(
      {
        type: "list",
        search,
      },
      { page, limit, pagination },
    ),
    dedupeInFlight: true,
  });

export const showCustomerUser = ({ id } = {}) =>
  erpFetch("/customer_users", {
    method: "POST",
    body: {
      type: "show",
      id,
    },
    dedupeInFlight: true,
  });

const withCustomerBranchId = (fields = {}) => {
  if (fields.branch_id !== undefined && fields.branch_id !== null) {
    return fields;
  }
  const branchId = getActiveBranchId();
  if (branchId == null) return fields;
  return { ...fields, branch_id: branchId };
};

export const createCustomerUser = (payload = {}) =>
  erpFetch("/customer_users", {
    method: "POST",
    body: {
      type: "create",
      ...withCustomerBranchId(payload),
    },
  });

export const updateCustomerUser = ({ id, ...fields } = {}) =>
  erpFetch("/customer_users", {
    method: "POST",
    body: {
      type: "update",
      id,
      ...withCustomerBranchId(fields),
    },
  });

/**
 * Multipart create/update for customer_users when attaching KYC docs.
 * File keys: aadhar_card, pan_card, gst_document (png/jpg/webp/gif/pdf).
 * Omit a file key on update to keep the existing document.
 */
const appendCustomerUserFields = (form, fields = {}) => {
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (value instanceof File) {
      form.append(key, value);
      return;
    }
    form.append(key, String(value));
  });
};

export const createCustomerUserMultipart = (fields = {}) => {
  const form = new FormData();
  form.append("type", "create");
  appendCustomerUserFields(form, withCustomerBranchId(fields));
  return postMultipartToRoute("/customer_users", form);
};

export const updateCustomerUserMultipart = ({ id, ...fields } = {}) => {
  const form = new FormData();
  form.append("type", "update");
  if (id != null) form.append("id", String(id));
  appendCustomerUserFields(form, withCustomerBranchId(fields));
  return postMultipartToRoute("/customer_users", form);
};

export const deactivateCustomerUser = ({ id } = {}) =>
  erpFetch("/customer_users", {
    method: "POST",
    body: {
      type: "deactivate",
      id,
    },
  });

// ---------- Product Trail endpoint ----------
export const searchProductTrail = ({ type, id } = {}) =>
  erpFetch("/product_trail", {
    method: "POST",
    body: {
      type,
      id,
    },
  });

// ---------- Old metal purchase (modular ERP, plain JSON) ----------
export const postShowOldMetalPurchase = (body = {}, { pinTypeOverride } = {}) =>
  erpFetch("/show_old_metal_purchase", {
    method: "POST",
    body,
    ...(pinTypeOverride != null ? { pinTypeOverride } : null),
  });

export const listOldMetalPurchases = ({
  search = "",
  page,
  limit,
  pagination,
  /** URD lives on /api even in quote PIN + mixed scope */
  pinTypeOverride = "access",
} = {}) =>
  postShowOldMetalPurchase(
    withListPagination(
      { type: "all", search },
      {
        page: page ?? pagination?.page ?? 1,
        limit: limit ?? pagination?.limit ?? 10,
      },
    ),
    { pinTypeOverride },
  );

export const showOldMetalPurchaseById = ({
  id,
  /** URD always uses /api (not /quote) */
  pinTypeOverride = "access",
} = {}) =>
  postShowOldMetalPurchase(
    {
      type: "show",
      id,
    },
    { pinTypeOverride },
  );

export const addOldMetalPurchase = (payload = {}) =>
  erpFetch("/add_old_metal_purchase", {
    method: "POST",
    body: payload,
  });

export const editOldMetalPurchase = (payload = {}) =>
  erpFetch("/edit_old_metal_purchase", {
    method: "POST",
    body: payload,
  });

// ---------- Sales (modular ERP) ----------
/** @param {object} opts
 *  @param {string} [opts.type="list"] list action; quote mode "All" on Invoice uses `"mixed"`.
 */
export const listSales = ({
  search = "",
  page,
  limit,
  pagination,
  type = "list",
} = {}) =>
  erpFetch("/sales", {
    method: "POST",
    body: withListPagination(
      {
        type: type || "list",
        search,
      },
      { page, limit, pagination },
    ),
  });

/**
 * Load one sale. In quote PIN + navbar "All", pass isQuote so SAL uses /api and QSAL uses /quote.
 * @param {object} opts
 * @param {number|string} opts.id
 * @param {boolean} [opts.isQuote] true → /quote/sales, false → /api/sales, omit → current PIN
 * @param {"quote"|"access"|null} [opts.pinTypeOverride]
 */
export const showSale = ({ id, isQuote, pinTypeOverride } = {}) => {
  let override = pinTypeOverride ?? null;
  if (override == null && isQuote !== undefined && isQuote !== null) {
    override = isQuote ? "quote" : "access";
  }
  return erpFetch("/sales", {
    method: "POST",
    body: {
      type: "show",
      id,
    },
    dedupeInFlight: true,
    pinTypeOverride: override,
  });
};

export const createSale = (payload = {}) =>
  erpFetch("/sales", {
    method: "POST",
    body: {
      type: "create",
      ...payload,
    },
  });

export const settlementSale = (payload = {}) =>
  erpFetch("/sales", {
    method: "POST",
    body: {
      type: "settlement",
      ...payload,
    },
  });

/** Pass fields the backend expects for `type: "update"` (legacy edit_sales body). */
export const updateSale = (payload = {}) =>
  erpFetch("/sales", {
    method: "POST",
    body: {
      type: "update",
      ...payload,
    },
  });

/** Store GST portal IRN / Ack / Signed QR against a sale (`type: "gst_portal"`). */
export const importSaleEinvoice = (payload = {}) =>
  erpFetch("/sales", {
    method: "POST",
    body: {
      type: "gst_portal",
      ...payload,
    },
  });

/**
 * Hard-delete a sale bill (physical delete).
 * Endpoints (same handler):
 * - Regular bill: POST /api/sales
 * - Quotation bill: POST /quote/sales
 *
 * Auth: same Bearer token as other sale calls.
 * Request body (send one style, not both mixed):
 *   By id: { type: "delete", id }
 *   By bill number: { type: "delete", doc_number, financial_year }
 *
 * @param {object} payload
 * @param {number|string} [payload.id]
 * @param {string} [payload.doc_number]
 * @param {string} [payload.financial_year]
 * @param {object} [options]
 * @param {boolean} [options.isQuote]
 */
export const deleteSale = (payload = {}, options = {}) => {
  const isQuote =
    options.isQuote !== undefined
      ? Boolean(options.isQuote)
      : options.quote !== undefined
        ? Boolean(options.quote)
        : payload.is_quote !== undefined
          ? Boolean(payload.is_quote)
          : payload.estimate !== undefined
            ? Number(payload.estimate) === 1
            : typeof payload.doc_number === "string" &&
              payload.doc_number.toUpperCase().startsWith("QSAL_");

  const pinTypeOverride =
    isQuote
      ? "quote"
      : options.isQuote === false
        ? "access"
        : null;

  // Prefer id when available
  const rawId = payload.id ?? payload.sale_id ?? payload.saleId;
  const numId = Number(rawId);
  const hasValidId =
    rawId !== undefined &&
    rawId !== null &&
    rawId !== "" &&
    Number.isFinite(numId) &&
    numId > 0;

  let body;
  if (hasValidId) {
    // Strictly by id: do not mix with doc_number or auto-inject financial_year
    body = {
      type: "delete",
      id: numId,
    };
  } else {
    // By bill number (financial_year is required with doc_number; format YY-YY, e.g. 26-27)
    const docNumber =
      payload.doc_number ??
      payload.docNumber ??
      payload.voucherNumber ??
      payload.voucher_number;

    const finYear =
      payload.financial_year ??
      payload.financialYear ??
      payload.fiscal_year_label ??
      getFinancialYear();

    body = {
      type: "delete",
      doc_number: docNumber != null ? String(docNumber).trim() : "",
      financial_year: finYear != null ? String(finYear).trim() : "",
    };
  }

  return erpFetch("/sales", {
    method: "POST",
    body,
    skipFinancialYear: true,
    pinTypeOverride,
  });
};

// ---------- Wholesale / Whsales (modular ERP) ----------
/** @param {object} opts */
export const listWhsales = ({
  search = "",
  page,
  limit,
  pagination,
  type = "list",
} = {}) =>
  erpFetch("/whsales", {
    method: "POST",
    body: withListPagination(
      {
        type: type || "list",
        search,
      },
      { page, limit, pagination },
    ),
  });

export const showWhsale = ({ id, doc_number, financial_year } = {}) => {
  const body = { type: "show" };
  if (id != null && id !== "") {
    body.id = id;
  } else {
    if (doc_number != null) body.doc_number = doc_number;
    if (financial_year != null) body.financial_year = financial_year;
  }
  return erpFetch("/whsales", {
    method: "POST",
    body,
    dedupeInFlight: true,
  });
};

export const createWhsale = (payload = {}) =>
  erpFetch("/whsales", {
    method: "POST",
    body: {
      type: "create",
      ...payload,
    },
  });

export const settlementWhsale = (payload = {}) =>
  erpFetch("/whsales", {
    method: "POST",
    body: {
      type: "settlement",
      ...payload,
    },
  });

/** Pass fields the backend expects for `type: "update"`. */
export const updateWhsale = (payload = {}) =>
  erpFetch("/whsales", {
    method: "POST",
    body: {
      type: "update",
      ...payload,
    },
  });

/**
 * Hard-delete a wholesale bill.
 * Body: { type: "delete", id } or { type: "delete", doc_number, financial_year }
 */
export const deleteWhsale = (payload = {}) => {
  const rawId = payload.id ?? payload.whsale_id ?? payload.whsaleId ?? payload.sale_id;
  const numId = Number(rawId);
  const hasValidId =
    rawId !== undefined &&
    rawId !== null &&
    rawId !== "" &&
    Number.isFinite(numId) &&
    numId > 0;

  let body;
  if (hasValidId) {
    body = {
      type: "delete",
      id: numId,
    };
  } else {
    const docNumber =
      payload.doc_number ??
      payload.docNumber ??
      payload.voucherNumber ??
      payload.voucher_number;

    const finYear =
      payload.financial_year ??
      payload.financialYear ??
      payload.fiscal_year_label ??
      getFinancialYear();

    body = {
      type: "delete",
      doc_number: docNumber != null ? String(docNumber).trim() : "",
      financial_year: finYear != null ? String(finYear).trim() : "",
    };
  }

  return erpFetch("/whsales", {
    method: "POST",
    body,
    skipFinancialYear: true,
  });
};

// ---------- Purchases (modular ERP) ----------
export const postPurchases = ({ type, ...payload } = {}) =>
  erpFetch("/purchases", {
    method: "POST",
    body: {
      type,
      ...payload,
    },
  });

export const listPurchases = ({
  search = "",
  page,
  limit,
  pagination,
} = {}) =>
  postPurchases(
    withListPagination(
      {
        type: "list",
        search,
      },
      { page, limit, pagination },
    ),
  );

export const showPurchase = ({ id } = {}) =>
  postPurchases({
    type: "show",
    id,
  });

export const createPurchase = (payload = {}) =>
  postPurchases({
    type: "create",
    ...payload,
  });

/**
 * Multipart POST /api/purchases (type: create).
 * - `items` and `payments` are JSON strings.
 * - `image_0`, `image_1`, … = file for items[0], items[1], … (only append when that line has a file).
 */
export const createPurchaseMultipartForm = (formData) => {
  const fd = new FormData();
  fd.append("type", "create");
  for (const [key, value] of formData.entries()) {
    fd.append(key, value);
  }
  return postMultipartToRoute("/purchases", fd);
};

export const updatePurchase = (payload = {}) =>
  postPurchases({
    type: "update",
    ...payload,
  });

export const settlementPurchase = (payload = {}) =>
  postPurchases({
    type: "settlement",
    ...payload,
  });

// ---------- Rate Cut ----------
export const getRateCutSummary = ({ supplier_id, branch_id } = {}) =>
  erpFetch("/ratecut", {
    method: "POST",
    body: {
      type: "summary",
      supplier_id,
      ...(branch_id !== undefined && branch_id !== null ? { branch_id } : null),
    },
  });

export const settleRateCut = (payload = {}) =>
  erpFetch("/ratecut", {
    method: "POST",
    body: payload,
  });

// ---------- Girvi (modular ERP) ----------
export const listGirvis = ({
  search = "",
  page,
  limit,
  pagination = { page: 1, limit: 10 },
} = {}) =>
  erpFetch("/girvi", {
    method: "POST",
    body: withListPagination(
      {
        type: "list",
        search,
      },
      {
        page: page ?? pagination?.page ?? 1,
        limit: limit ?? pagination?.limit ?? 10,
      },
    ),
    dedupeInFlight: true,
  });

export const showGirvi = ({ id } = {}) =>
  erpFetch("/girvi", {
    method: "POST",
    body: { type: "show", id },
    dedupeInFlight: true,
  });

const postGirviMultipart = (formData) => {
  const baseUrl = getErpBaseUrl();
  const url = `${baseUrl}/girvi`;
  const token = getErpToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const financialYear = getFinancialYear();
  if (
    financialYear &&
    formData instanceof FormData &&
    !formData.has("financial_year")
  ) {
    formData.append("financial_year", financialYear);
  }
  return axiosErp
    .request({
      url,
      method: "POST",
      headers,
      data: formData,
    })
    .then((res) => res.data);
};

export const createGirviMultipart = (formData) => postGirviMultipart(formData);
export const updateGirviMultipart = (formData) => postGirviMultipart(formData);

export const settlementGirvi = (payload = {}) =>
  erpFetch("/girvi", {
    method: "POST",
    body: {
      type: "settlement",
      ...payload,
    },
  });

// ---------- Reverse Girvi / Reverse Dihran ----------
export const listReverseGirvis = ({
  search = "",
  page,
  limit,
  pagination = { page: 1, limit: 10 },
} = {}) =>
  erpFetch("/reverse_girvi", {
    method: "POST",
    body: withListPagination(
      {
        type: "list",
        search,
      },
      {
        page: page ?? pagination?.page ?? 1,
        limit: limit ?? pagination?.limit ?? 10,
      },
    ),
    dedupeInFlight: true,
  });

export const showReverseGirvi = ({ id } = {}) =>
  erpFetch("/reverse_girvi", {
    method: "POST",
    body: { type: "show", id },
    dedupeInFlight: true,
  }).then(aliasReverseShowResponse);

const postReverseGirviMultipart = (formData) => {
  const fd = translateReverseGirviFormData(formData);
  const baseUrl = getErpBaseUrl();
  const url = `${baseUrl}/reverse_girvi`;
  const token = getErpToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const financialYear = getFinancialYear();
  if (financialYear && fd instanceof FormData && !fd.has("financial_year")) {
    fd.append("financial_year", financialYear);
  }
  return axiosErp
    .request({
      url,
      method: "POST",
      headers,
      data: fd,
    })
    .then((res) => aliasReverseMutationResponse(res.data));
};

export const createReverseGirviMultipart = (formData) =>
  postReverseGirviMultipart(formData);
export const updateReverseGirviMultipart = (formData) =>
  postReverseGirviMultipart(formData);

export const settlementReverseGirvi = (payload = {}) =>
  erpFetch("/reverse_girvi", {
    method: "POST",
    body: translateReverseSettlementPayload(payload),
  }).then(aliasReverseMutationResponse);

export const postReverseGirviDashboard = (body) =>
  erpFetch("/reverse_girvi_dashboard", {
    method: "POST",
    body,
    dedupeInFlight: true,
  });

// ---------- Girvi bank endpoints ----------
const GIRVI_BANKS_LIST_DEDUPE_MS = 2000;
let girviBanksListCache = null;
let girviBanksListCacheAt = 0;
let girviBanksListInFlight = null;

export const listGirviBanks = ({ id } = {}) => {
  const hasId = id !== undefined && id !== null;

  // Default list (pageload) — share one request + brief cache across Strict Mode remounts
  if (!hasId) {
    const now = Date.now();
    if (
      girviBanksListCache &&
      now - girviBanksListCacheAt < GIRVI_BANKS_LIST_DEDUPE_MS
    ) {
      return Promise.resolve(girviBanksListCache);
    }
    if (girviBanksListInFlight) return girviBanksListInFlight;

    girviBanksListInFlight = erpFetch("/girvi_bank", {
      method: "POST",
      body: { type: "list" },
      dedupeInFlight: true,
    })
      .then((res) => {
        girviBanksListCache = res;
        girviBanksListCacheAt = Date.now();
        return res;
      })
      .finally(() => {
        girviBanksListInFlight = null;
      });

    return girviBanksListInFlight;
  }

  return erpFetch("/girvi_bank", {
    method: "POST",
    body: {
      type: "list",
      id,
    },
    dedupeInFlight: true,
  });
};

export function clearGirviBanksListCache() {
  girviBanksListCache = null;
  girviBanksListCacheAt = 0;
  girviBanksListInFlight = null;
}

/** Same field set as `createBankAccount` (BankDetailsTable); `bank_name` normalized from `name` if needed. */
export const createGirviBank = (payload = {}) => {
  clearGirviBanksListCache();
  const { name, bank_name: bankNameField, ...fields } = payload;
  const resolvedBankName = String(bankNameField ?? name ?? "").trim();
  return erpFetch("/girvi_bank", {
    method: "POST",
    body: {
      type: "create",
      bank_name: resolvedBankName,
      ...fields,
    },
  });
};

// ---------- Custom Orders (modular ERP) ----------
/** @param {string} [opts.type="list"] quote navbar "All" uses `"mixed"`. */
export const listCustomOrders = ({
  search = "",
  page,
  limit,
  pagination,
  type = "list",
  isQuote,
  pinTypeOverride,
} = {}) => {
  let override = pinTypeOverride ?? null;
  if (override == null && isQuote !== undefined && isQuote !== null) {
    override = isQuote ? "quote" : "access";
  }
  return erpFetch("/custom_orders", {
    method: "POST",
    body: withListPagination(
      {
        type: type || "list",
        ...(search !== undefined ? { search } : null),
      },
      { page, limit, pagination },
    ),
    ...(override != null ? { pinTypeOverride: override } : null),
  });
};

/**
 * @param {object} opts
 * @param {number|string} opts.id
 * @param {boolean} [opts.isQuote] true → /quote, false → /api, omit → current PIN
 * @param {"quote"|"access"|null} [opts.pinTypeOverride]
 */
export const showCustomOrder = ({ id, isQuote, pinTypeOverride } = {}) => {
  let override = pinTypeOverride ?? null;
  if (override == null && isQuote !== undefined && isQuote !== null) {
    override = isQuote ? "quote" : "access";
  }
  return erpFetch("/custom_orders", {
    method: "POST",
    body: {
      type: "show",
      id,
    },
    dedupeInFlight: true,
    pinTypeOverride: override,
  });
};

export const createCustomOrder = (payload = {}) =>
  erpFetch("/custom_orders", {
    method: "POST",
    body: {
      type: "create",
      ...payload,
    },
  });

export const updateCustomOrder = (payload = {}) =>
  erpFetch("/custom_orders", {
    method: "POST",
    body: {
      type: "update",
      ...payload,
    },
  });

export const settlementCustomOrder = (payload = {}) =>
  erpFetch("/custom_orders", {
    method: "POST",
    body: {
      type: "settlement",
      ...payload,
    },
  });

export const redeemCustomOrder = (payload = {}) =>
  erpFetch("/custom_orders", {
    method: "POST",
    body: {
      type: "redeem",
      ...payload,
    },
  });

export const assignKarigarToCustomOrderItem = (payload = {}) =>
  erpFetch("/custom_orders", {
    method: "POST",
    body: {
      type: "assign_karigar",
      ...payload,
    },
  });

export const receiveCustomOrderItem = (payload = {}) =>
  erpFetch("/custom_orders", {
    method: "POST",
    body: {
      type: "receive",
      ...payload,
    },
  });

// ---------- Repairing Orders (modular ERP) ----------
/** @param {string} [opts.type="list"] quote navbar "All" uses `"mixed"`. */
export const listRepairingOrders = ({
  search = "",
  page,
  limit,
  pagination,
  type = "list",
  isQuote,
  pinTypeOverride,
} = {}) => {
  let override = pinTypeOverride ?? null;
  if (override == null && isQuote !== undefined && isQuote !== null) {
    override = isQuote ? "quote" : "access";
  }
  return erpFetch("/repairing_orders", {
    method: "POST",
    body: withListPagination(
      {
        type: type || "list",
        ...(search !== undefined ? { search } : null),
      },
      { page, limit, pagination },
    ),
    ...(override != null ? { pinTypeOverride: override } : null),
  });
};

/**
 * @param {object} opts
 * @param {number|string} opts.id
 * @param {boolean} [opts.isQuote] true → /quote, false → /api, omit → current PIN
 * @param {"quote"|"access"|null} [opts.pinTypeOverride]
 */
export const showRepairingOrder = ({ id, isQuote, pinTypeOverride } = {}) => {
  let override = pinTypeOverride ?? null;
  if (override == null && isQuote !== undefined && isQuote !== null) {
    override = isQuote ? "quote" : "access";
  }
  return erpFetch("/repairing_orders", {
    method: "POST",
    body: { type: "show", id },
    dedupeInFlight: true,
    pinTypeOverride: override,
  });
};

export const createRepairingOrder = (payload = {}) =>
  erpFetch("/repairing_orders", {
    method: "POST",
    body: { type: "create", ...payload },
  });

export const updateRepairingOrder = (payload = {}) =>
  erpFetch("/repairing_orders", {
    method: "POST",
    body: { type: "update", ...payload },
  });

const postRepairingOrdersMultipart = (formData) => {
  const baseUrl = getErpBaseUrl();
  const url = `${baseUrl}/repairing_orders`;
  const token = getErpToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const financialYear = getFinancialYear();
  if (
    financialYear &&
    formData instanceof FormData &&
    !formData.has("financial_year")
  ) {
    formData.append("financial_year", financialYear);
  }

  return axiosErp
    .request({
      url,
      method: "POST",
      headers,
      data: formData,
    })
    .then((res) => res.data);
};

const ensureRepairingOrderFormDataType = (formData, type) => {
  if (!(formData instanceof FormData)) return formData;
  for (const k of formData.keys()) {
    if (k === "type") return formData;
  }
  formData.append("type", type);
  return formData;
};

/** Multipart POST /api/repairing_orders — ensures `type: create` is present for the handler. */
export const createRepairingOrderMultipart = (formData) => {
  ensureRepairingOrderFormDataType(formData, "create");
  return postRepairingOrdersMultipart(formData);
};

/** Multipart POST /api/repairing_orders — ensures `type: update` is present for the handler. */
export const updateRepairingOrderMultipart = (formData) => {
  ensureRepairingOrderFormDataType(formData, "update");
  return postRepairingOrdersMultipart(formData);
};

export const assignKarigarToRepairingOrderItem = (payload = {}) =>
  erpFetch("/repairing_orders", {
    method: "POST",
    body: { type: "assign_karigar", ...payload },
  });

export const receivedAndUpdateRepairingOrderItem = (payload = {}) =>
  erpFetch("/repairing_orders", {
    method: "POST",
    body: { type: "received_and_update", ...payload },
  });

export const settlementRepairingOrder = (payload = {}) =>
  erpFetch("/repairing_orders", {
    method: "POST",
    body: {
      type: "settlement",
      ...payload,
    },
  });

export const productDeliveryRepairingOrder = (payload = {}) =>
  erpFetch("/repairing_orders", {
    method: "POST",
    body: {
      type: "product_delivery",
      ...payload,
    },
  });

// ---------- Jangads (modular ERP) ----------
export const postJangads = ({ type, ...payload } = {}) =>
  erpFetch("/jangads", {
    method: "POST",
    body: {
      type,
      ...payload,
    },
  });

export const listJangads = ({
  search = "",
  page,
  limit,
  pagination,
} = {}) =>
  postJangads(
    withListPagination(
      {
        type: "list",
        search,
      },
      { page, limit, pagination },
    ),
  );

export const showJangad = ({ id } = {}) =>
  erpFetch("/jangads", {
    method: "POST",
    body: { type: "show", id },
    dedupeInFlight: true,
  });

export const createJangad = (payload = {}) =>
  postJangads({
    type: "create",
    ...payload,
  });

export const updateJangad = (payload = {}) =>
  postJangads({
    type: "update",
    ...payload,
  });

export const settlementJangad = (payload = {}) =>
  postJangads({
    type: "settlement",
    ...payload,
  });

// ---------- Refinery endpoints ----------

export const postRefinery = ({ type, ...payload } = {}) =>
  erpFetch("/refinery", {
    method: "POST",
    body: { type, ...payload },
  });

export const listRefineries = ({
  search = "",
  pagination = { page: 1, limit: 10 },
  branch_id,
} = {}) =>
  postRefinery({
    type: "list",
    search,
    pagination,
    ...(branch_id != null ? { branch_id } : null),
  });

export const showRefinery = ({ id, doc_number, financial_year } = {}) =>
  postRefinery({
    type: "show",
    ...(id != null ? { id } : null),
    ...(doc_number ? { doc_number } : null),
    ...(financial_year ? { financial_year } : null),
  });

export const createRefinery = (payload = {}, { idempotencyKey } = {}) =>
  erpFetch("/refinery", {
    method: "POST",
    body: { type: "create", ...payload },
    idempotencyKey,
  });

export const updateRefinery = (payload = {}, { idempotencyKey } = {}) =>
  erpFetch("/refinery", {
    method: "POST",
    body: { type: "update", ...payload },
    idempotencyKey,
  });

// ---------- Tenant endpoints ----------

export const listTenants = () =>
  erpFetch("/tenant", {
    method: "POST",
    body: { type: "list" },
    dedupeInFlight: true,
  });

export const showTenant = ({ id } = {}) =>
  erpFetch("/tenant", {
    method: "POST",
    body: { type: "show", id },
    dedupeInFlight: true,
  });

export const createTenant = ({ name } = {}) =>
  erpFetch("/tenant", {
    method: "POST",
    body: { type: "create", name },
  });

/**
 * Create tenant with optional image files (multipart/form-data).
 * Only include an image key when the user picked a new file (File object)
 * or cleared it (empty string ""). Omit the key entirely to leave unchanged.
 * @param {{ name: string, logo?: File|string, favicon?: File|string, sidebar_icon?: File|string, signature?: File|string }} payload
 */
export const createTenantWithBranding = (payload = {}) => {
  const { name, logo, favicon, sidebar_icon, signature } = payload;
  const form = new FormData();
  form.append("type", "create");
  if (name != null) form.append("name", String(name));
  if (logo !== undefined) form.append("logo", logo instanceof File ? logo : "");
  if (favicon !== undefined) form.append("favicon", favicon instanceof File ? favicon : "");
  if (sidebar_icon !== undefined) form.append("sidebar_icon", sidebar_icon instanceof File ? sidebar_icon : "");
  if (signature !== undefined) form.append("signature", signature instanceof File ? signature : "");

  const baseUrl = getErpBaseUrl();
  const token = getErpToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  return fetch(`${baseUrl}/tenant`, { method: "POST", headers, body: form })
    .then((r) => r.json());
};

/**
 * Update tenant with optional image files (multipart/form-data).
 * @param {{ id: number, name?: string, logo?: File|string, favicon?: File|string, sidebar_icon?: File|string, signature?: File|string }} payload
 */
export const updateTenantWithBranding = (payload = {}) => {
  const { id, name, logo, favicon, sidebar_icon, signature } = payload;
  const form = new FormData();
  form.append("type", "update");
  form.append("id", String(id));
  if (name !== undefined) form.append("name", String(name));
  if (logo !== undefined) form.append("logo", logo instanceof File ? logo : "");
  if (favicon !== undefined) form.append("favicon", favicon instanceof File ? favicon : "");
  if (sidebar_icon !== undefined) form.append("sidebar_icon", sidebar_icon instanceof File ? sidebar_icon : "");
  if (signature !== undefined) form.append("signature", signature instanceof File ? signature : "");

  const baseUrl = getErpBaseUrl();
  const token = getErpToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  return fetch(`${baseUrl}/tenant`, { method: "POST", headers, body: form })
    .then((r) => r.json());
};

// ---------- Branch endpoints ----------

export const listBranches = () =>
  erpFetch("/branch", {
    method: "POST",
    body: { type: "list" },
    dedupeInFlight: true,
  });

export const showBranch = ({ id, branch_id } = {}) => {
  const bid = id ?? branch_id;
  return erpFetch("/branch", {
    method: "POST",
    body: {
      type: "show",
      ...(bid != null ? { id: bid, branch_id: bid } : null),
    },
    dedupeInFlight: true,
  });
};

// ---------- Roles endpoints ----------

export const listRoles = () =>
  erpFetch("/roles", {
    method: "POST",
    body: { type: "list" },
    dedupeInFlight: true,
  });

export const getRole = ({ id }) =>
  erpFetch("/roles", {
    method: "POST",
    body: { type: "get", id },
    dedupeInFlight: true,
  });

export const createRole = ({ name, permission_codes }) =>
  erpFetch("/roles", {
    method: "POST",
    body: {
      type: "create",
      name,
      permission_codes: sanitizePermissionCodes(permission_codes),
    },
  });

export const updateRole = ({ id, name, permission_codes }) =>
  erpFetch("/roles", {
    method: "POST",
    body: {
      type: "update",
      id,
      ...(name !== undefined ? { name } : null),
      ...(permission_codes !== undefined
        ? {
            permission_codes: sanitizePermissionCodes(permission_codes),
          }
        : null),
    },
  });

export const deleteRole = ({ id }) =>
  erpFetch("/roles", {
    method: "POST",
    body: { type: "delete", id },
  });

// ---------- Admin User endpoints ----------

export const listAdminUsers = ({ search } = {}) =>
  erpFetch("/admin_user", {
    method: "POST",
    body: {
      type: "list",
      ...(search !== undefined && search !== null && String(search).trim() !== ""
        ? { search: String(search).trim() }
        : null),
    },
    dedupeInFlight: true,
  });

/** Full permission catalog for role / employee checklists. */
export const listAdminUserPermissions = () =>
  erpFetch("/admin_user", {
    method: "POST",
    body: { type: "list_permissions" },
    dedupeInFlight: true,
  });

export const updateAdminUser = ({
  id,
  name,
  phone,
  email,
  status,
  branch_id,
  role_ids,
  permission_codes,
  branch_accesses,
} = {}) =>
  erpFetch("/admin_user", {
    method: "POST",
    body: {
      type: "update",
      id,
      name,
      phone,
      email,
      status,
      ...(branch_id !== undefined ? { branch_id } : null),
      ...(role_ids !== undefined ? { role_ids } : null),
      ...(permission_codes !== undefined
        ? {
            permission_codes: sanitizePermissionCodes(permission_codes),
          }
        : null),
      ...(branch_accesses !== undefined
        ? {
            branch_accesses: sanitizeBranchAccessesForErp(branch_accesses),
          }
        : null),
    },
  });

export const setAdminUserBranchAccesses = ({ id, branch_accesses }) =>
  erpFetch("/admin_user", {
    method: "POST",
    body: {
      type: "set_branch_accesses",
      id,
      branch_accesses: sanitizeBranchAccessesForErp(branch_accesses),
    },
  });

export const getAdminUser = ({ id }) =>
  erpFetch("/admin_user", {
    method: "POST",
    body: {
      type: "get",
      id,
    },
    dedupeInFlight: true,
  });

/** Approve a pending user (assign tenant + branch accesses). Includes `financial_year` when set. */
export const acceptAdminUser = ({
  id,
  tenant_id,
  branch_id,
  role_ids,
  permission_codes,
  branch_accesses,
  financial_year,
} = {}) => {
  const fy =
    financial_year != null && String(financial_year).trim() !== ""
      ? String(financial_year).trim()
      : getFinancialYear();

  return erpFetch("/admin_user", {
    method: "POST",
    body: {
      type: "accept_user",
      id,
      tenant_id,
      ...(branch_id !== undefined ? { branch_id } : null),
      ...(role_ids !== undefined ? { role_ids } : null),
      ...(permission_codes !== undefined
        ? {
            permission_codes: sanitizePermissionCodes(permission_codes),
          }
        : null),
      ...(branch_accesses !== undefined
        ? {
            branch_accesses: sanitizeBranchAccessesForErp(branch_accesses),
          }
        : null),
      ...(fy ? { financial_year: fy } : {}),
    },
  });
};

/** Reject / remove a pending user. */
export const rejectAdminUser = ({ id }) =>
  erpFetch("/admin_user", {
    method: "POST",
    body: {
      type: "reject_user",
      id,
    },
  });

// ---------- Core Product endpoints ----------

export const createCoreProduct = ({ name, status }) =>
  erpFetch("/core_product", {
    method: "POST",
    body: { type: "create", name, status },
  });

export const updateCoreProduct = ({ id, name, status }) =>
  erpFetch("/core_product", {
    method: "POST",
    body: { type: "update", id, name, status },
  });

export const listCoreProducts = () =>
  erpFetch("/core_product", {
    method: "POST",
    body: { type: "list" },
  });

// ---------- Metal Group endpoints ----------

export const createMetalGroup = ({
  group_name,
  metal_type,
  carat,
  purity,
  hsn_code,
  short_code,
  status,
}) =>
  erpFetch("/metal_group", {
    method: "POST",
    body: {
      type: "create",
      group_name,
      metal_type,
      carat,
      purity,
      hsn_code,
      short_code,
      status,
    },
  });

export const updateMetalGroup = ({
  id,
  group_name,
  metal_type,
  carat,
  purity,
  hsn_code,
  short_code,
  status,
}) => {
  const payload = {
    type: "update",
    id,
    group_name,
    metal_type,
    carat,
    purity,
    hsn_code,
    short_code,
    status,
  };

  // console.log("[updateMetalGroup] request", { path: "/metal_group", payload });

  return erpFetch("/metal_group", {
    method: "POST",
    body: payload,
  })
    .then((res) => {
      // console.log("[updateMetalGroup] response", res);
      return res;
    })
    .catch((err) => {
      // console.error("[updateMetalGroup] error", {
      //   message: err?.message,
      //   status: err?.response?.status,
      //   data: err?.response?.data,
      // });
      throw err;
    });
};

export const listMetalGroups = ({ search = "", page, limit, pagination } = {}) =>
  erpFetch("/metal_group", {
    method: "POST",
    body: withListPagination(
      { type: "list", ...(search ? { search } : null) },
      { page, limit, pagination },
    ),
  });

export const showMetalGroup = ({ id } = {}) =>
  erpFetch("/metal_group", {
    method: "POST",
    body: {
      type: "show",
      id,
    },
  });

// ---------- Item Category endpoints ----------

export const createItemCategory = ({
  name,
  short_code,
  status,
  core_product_id,
}) =>
  erpFetch("/item_category", {
    method: "POST",
    body: { type: "create", name, short_code, status, core_product_id },
  });

export const updateItemCategory = ({
  id,
  name,
  short_code,
  status,
  core_product_id,
}) =>
  erpFetch("/item_category", {
    method: "POST",
    body: { type: "update", id, name, short_code, status, core_product_id },
  });

export const listItemCategories = ({
  search = "",
  page,
  limit,
  pagination,
} = {}) =>
  erpFetch("/item_category", {
    method: "POST",
    body: withListPagination(
      { type: "list", ...(search ? { search } : null) },
      { page, limit, pagination },
    ),
  });

// ---------- Other Charges endpoints ----------
// List/show follow the current pin (access → /api, quote → /quote, read-only).
// Create/update always write to /api.

const otherChargeBody = (type, payload) => ({
  type,
  ...payload,
});

export const createOtherCharge = ({
  name,
  code,
  rate_on,
  rate,
  status,
} = {}) =>
  erpFetch("/other_charges", {
    method: "POST",
    pinTypeOverride: "access",
    skipFinancialYear: true,
    body: otherChargeBody("create", {
      name,
      code,
      rate_on,
      rate,
      ...(status !== undefined ? { status } : null),
    }),
  });

export const updateOtherCharge = ({
  id,
  name,
  code,
  rate_on,
  rate,
  status,
} = {}) =>
  erpFetch("/other_charges", {
    method: "POST",
    pinTypeOverride: "access",
    skipFinancialYear: true,
    body: otherChargeBody("update", {
      id,
      name,
      code,
      rate_on,
      rate,
      ...(status !== undefined ? { status } : null),
    }),
  });

export const listOtherCharges = ({
  search = "",
  page,
  limit,
  pagination,
} = {}) =>
  erpFetch("/other_charges", {
    method: "POST",
    skipFinancialYear: true,
    dedupeInFlight: true,
    body: withListPagination(
      { type: "list", ...(search ? { search } : null) },
      { page, limit, pagination },
    ),
  });

export const showOtherCharge = ({ id } = {}) =>
  erpFetch("/other_charges", {
    method: "POST",
    skipFinancialYear: true,
    body: { type: "show", id },
  });

// ---------- Product Group endpoints ----------

export const listProductGroups = ({
  item_category_id,
  metal_group_id,
  branch_id,
  search,
  page,
  limit,
  pagination,
} = {}) => {
  const bid = branch_id ?? resolveErpSessionBranchId();
  const searchText =
    search !== undefined && search !== null ? String(search).trim() : "";
  return erpFetch("/product_group", {
    method: "POST",
    body: withListPagination(
      {
        type: "list",
        ...(bid != null ? { branch_id: bid } : null),
        ...(item_category_id ? { item_category_id } : null),
        ...(metal_group_id ? { metal_group_id } : null),
        ...(searchText ? { search: searchText } : null),
      },
      { page, limit, pagination },
    ),
    dedupeInFlight: true,
  });
};

export const showProductGroup = ({ id, branch_id } = {}) => {
  const bid = branch_id ?? resolveErpSessionBranchId();
  return erpFetch("/product_group", {
    method: "POST",
    body: {
      type: "show",
      id,
      ...(bid != null ? { branch_id: bid } : null),
    },
    dedupeInFlight: true,
  });
};

export const setOpeningProductGroupPool = (body, { idempotencyKey } = {}) =>
  erpFetch("/product_group", {
    method: "POST",
    body: { type: "set_opening_pool", ...body },
    idempotencyKey,
  });

// ---------- Product (Stock) endpoints ----------

export const listProducts = ({
  page,
  limit,
  search = "",
  stock_type,
  from_date,
  to_date,
} = {}) =>
  (() => {
    const searchText =
      search !== undefined && search !== null ? String(search).trim() : "";
    const dateFields = {
      ...(from_date ? { from_date } : null),
      ...(to_date ? { to_date } : null),
    };
    if (searchText) {
      // Search requests should not send pagination/stock filters.
      return erpFetch("/product", {
        method: "POST",
        body: {
          type: "list",
          search: searchText,
          ...dateFields,
        },
        dedupeInFlight: true,
      });
    }

    return erpFetch("/product", {
      method: "POST",
      body: {
        type: "list",
        ...(search !== undefined ? { search } : null),
        ...(page !== undefined && page !== null ? { page } : null),
        ...(limit !== undefined && limit !== null ? { limit } : null),
        ...(stock_type !== undefined && stock_type !== null
          ? { stock_type }
          : null),
        ...dateFields,
      },
      dedupeInFlight: true,
    });
  })();

export const showProducts = ({ id, tag_no } = {}) => {
  const body = { type: "show" };
  if (Array.isArray(id)) {
    body.id = id.join(",");
  } else if (id !== undefined && id !== null && id !== "") {
    body.id = id;
  }
  const tag = tag_no != null ? String(tag_no).trim() : "";
  if (tag) {
    body.tag_no = tag;
  }
  return erpFetch("/product", {
    method: "POST",
    body,
    dedupeInFlight: true,
  });
};

/** Order queue list (legacy route name under /api) */
export const showOrderQueue = (payload = {}) =>
  erpFetch("/show_order_queue", {
    method: "POST",
    body: {
      type: "show",
      ...payload,
    },
  });

/** Paginated order queue list */
export const listOrderQueue = ({
  search = "",
  page,
  limit,
  pagination,
} = {}) =>
  erpFetch("/show_order_queue", {
    method: "POST",
    body: withListPagination(
      {
        type: "list",
        ...(search ? { search } : null),
      },
      { page, limit, pagination },
    ),
  });

/** Lock / unlock / status on order_queue (modular JSON API) */
export const postOrderQueue = (payload = {}) =>
  erpFetch("/order_queue", {
    method: "POST",
    body: payload,
  });

export const postOrderQueueMultipart = (formData) => {
  const baseUrl = getErpBaseUrl();
  const url = `${baseUrl}/order_queue`;
  const token = getErpToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return axiosErp.post(url, formData, { headers }).then((r) => r.data);
};

/** JSON body: API treats numeric 0 as missing; send weight zero as string "0". */
function erpProductWeight(value, fallback = 0) {
  const n = Number(value);
  const v = Number.isFinite(n) ? n : Number(fallback) || 0;
  return v === 0 ? "0" : v;
}

/** Normalize one create_single product row (JSON fields only; files stay separate). */
function normalizeCreateSingleProductItem(item = {}) {
  const {
    tag_no,
    product_group_id,
    pcs,
    huid,
    design,
    size,
    supplier_id,
    image,
    status,
    gwt,
    lwt_vepari,
    lwt_customer,
    nwt,
    lbr_type,
    lbr_percent,
    lbr_rate,
    lbr_amt,
    oth_amt,
    mrp,
    charges,
  } = item;

  return {
    ...(tag_no !== undefined &&
    tag_no !== null &&
    String(tag_no).trim() !== ""
      ? { tag_no: String(tag_no).trim() }
      : null),
    product_group_id,
    pcs,
    huid,
    design,
    size,
    supplier_id,
    ...(status !== undefined ? { status } : null),
    ...(gwt !== undefined ? { gwt: erpProductWeight(gwt) } : null),
    ...(lwt_vepari !== undefined
      ? { lwt_vepari: erpProductWeight(lwt_vepari) }
      : null),
    ...(lwt_customer !== undefined
      ? { lwt_customer: erpProductWeight(lwt_customer) }
      : null),
    nwt: erpProductWeight(nwt),
    ...(lbr_type !== undefined ? { lbr_type } : null),
    ...(lbr_percent !== undefined ? { lbr_percent } : null),
    ...(lbr_rate !== undefined ? { lbr_rate } : null),
    ...(lbr_amt !== undefined ? { lbr_amt } : null),
    ...(oth_amt !== undefined ? { oth_amt } : null),
    ...(mrp !== undefined ? { mrp } : null),
    ...(image ? { image } : null),
    ...(Array.isArray(charges) ? { charges } : null),
  };
}

/**
 * POST /product type create_single.
 * Prefers `items: [...]` (backend inserts each row). Flat single-product
 * args are still accepted and wrapped into a one-item `items` array.
 * Multipart: `items` JSON + `image_0`, `image_1`, … when rows have files.
 */
export const createSingleStock = ({
  branch_id,
  items,
  tag_no,
  product_group_id,
  pcs,
  huid,
  design,
  size,
  supplier_id,
  image,
  status,
  gwt,
  lwt_vepari,
  lwt_customer,
  nwt,
  lbr_type,
  lbr_percent,
  lbr_rate,
  lbr_amt,
  oth_amt,
  mrp,
  charges,
  image_file,
} = {}) =>
  (() => {
    const rawItems = Array.isArray(items)
      ? items
      : [
          {
            tag_no,
            product_group_id,
            pcs,
            huid,
            design,
            size,
            supplier_id,
            image,
            status,
            gwt,
            lwt_vepari,
            lwt_customer,
            nwt,
            lbr_type,
            lbr_percent,
            lbr_rate,
            lbr_amt,
            oth_amt,
            mrp,
            charges,
            image_file,
          },
        ];

    const imageFiles = rawItems.map((p) => p?.image_file || null);
    const itemRows = rawItems.map((p) => normalizeCreateSingleProductItem(p));

    const payload = {
      type: "create_single",
      branch_id,
      items: itemRows,
    };

    const hasImageFile = imageFiles.some(Boolean);
    if (hasImageFile) {
      const formData = new FormData();
      formData.append("type", "create_single");
      if (branch_id !== undefined && branch_id !== null) {
        formData.append("branch_id", String(branch_id));
      }
      formData.append("items", JSON.stringify(itemRows));
      imageFiles.forEach((file, index) => {
        if (!file) return;
        formData.append(`image_${index}`, file, file.name || `image_${index}`);
      });

      const baseUrl = getErpBaseUrl();
      const url = `${baseUrl}/product`;
      const token = getErpToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      return axiosErp
        .request({
          url,
          method: "POST",
          headers,
          data: formData,
        })
        .then((res) => res.data);
    }

    return erpFetch("/product", {
      method: "POST",
      body: payload,
    });
  })();

export const createBulkStock = ({
  branch_id,
  product_group_id,
  pcs,
  huid,
  design,
  size,
  image,
  supplier_id,
  gwt,
  lwt_vepari,
  lwt_customer,
  nwt,
  lbr_type,
  lbr_percent,
  lbr_rate,
  lbr_amt,
  oth_amt,
  mrp,
  charges,
  image_file,
} = {}) =>
  (() => {
    const payload = {
      type: "create_bulk",
      branch_id,
      product_group_id,
      pcs,
      huid,
      design,
      size,
      ...(image !== undefined ? { image } : null),
      supplier_id,
      ...(gwt !== undefined ? { gwt: erpProductWeight(gwt) } : null),
      ...(lwt_vepari !== undefined
        ? { lwt_vepari: erpProductWeight(lwt_vepari) }
        : null),
      ...(lwt_customer !== undefined
        ? { lwt_customer: erpProductWeight(lwt_customer) }
        : null),
      nwt: erpProductWeight(nwt),
      ...(lbr_type !== undefined ? { lbr_type } : null),
      ...(lbr_percent !== undefined ? { lbr_percent } : null),
      ...(lbr_rate !== undefined ? { lbr_rate } : null),
      ...(lbr_amt !== undefined ? { lbr_amt } : null),
      ...(oth_amt !== undefined ? { oth_amt } : null),
      ...(mrp !== undefined ? { mrp } : null),
      ...(Array.isArray(charges) ? { charges } : null),
    };

    if (image_file) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "image") return;
        if (key === "charges") {
          formData.append("charges", JSON.stringify(value));
          return;
        }
        formData.append(key, String(value));
      });
      formData.append("image", image_file, image_file.name || "image");

      const baseUrl = getErpBaseUrl();
      const url = `${baseUrl}/product`;
      const token = getErpToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      return axiosErp
        .request({
          url,
          method: "POST",
          headers,
          data: formData,
        })
        .then((res) => res.data);
    }

    return erpFetch("/product", {
      method: "POST",
      body: payload,
    });
  })();

export const updateStock = ({
  id,
  branch_id,
  product_group_id,
  pcs,
  current_pcs,
  huid,
  design,
  size,
  image,
  supplier_id,
  status,
  gwt,
  lwt_vepari,
  lwt_customer,
  nwt,
  lbr_type,
  lbr_percent,
  lbr_rate,
  lbr_amt,
  oth_amt,
  mrp,
  charges,
  image_file,
} = {}) =>
  (() => {
    const payload = {
      type: "update",
      id,
      branch_id,
      product_group_id,
      pcs,
      current_pcs,
      huid,
      design,
      size,
      ...(image !== undefined ? { image } : null),
      supplier_id,
      ...(status !== undefined ? { status } : null),
      ...(gwt !== undefined ? { gwt } : null),
      ...(lwt_vepari !== undefined ? { lwt_vepari } : null),
      ...(lwt_customer !== undefined ? { lwt_customer } : null),
      ...(nwt !== undefined ? { nwt } : null),
      ...(lbr_type !== undefined ? { lbr_type } : null),
      ...(lbr_percent !== undefined ? { lbr_percent } : null),
      ...(lbr_rate !== undefined ? { lbr_rate } : null),
      ...(lbr_amt !== undefined ? { lbr_amt } : null),
      ...(oth_amt !== undefined ? { oth_amt } : null),
      ...(mrp !== undefined ? { mrp } : null),
      ...(Array.isArray(charges) ? { charges } : null),
    };

    if (image_file) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "image") return;
        if (key === "charges") {
          formData.append("charges", JSON.stringify(value));
          return;
        }
        formData.append(key, String(value));
      });
      formData.append("image", image_file, image_file.name || "image");

      const baseUrl = getErpBaseUrl();
      const url = `${baseUrl}/product`;
      const token = getErpToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      return axiosErp
        .request({
          url,
          method: "POST",
          headers,
          data: formData,
        })
        .then((res) => res.data);
    }

    return erpFetch("/product", {
      method: "POST",
      body: payload,
    });
  })();

export const createProductGroup = ({
  metal_group_id,
  item_category_id,
  name,
  short_code,
}) =>
  erpFetch("/product_group", {
    method: "POST",
    body: {
      type: "create",
      metal_group_id,
      item_category_id,
      name,
      short_code,
    },
  });

export const updateProductGroup = ({
  id,
  metal_group_id,
  item_category_id,
  name,
  short_code,
}) =>
  erpFetch("/product_group", {
    method: "POST",
    body: {
      type: "update",
      id,
      metal_group_id,
      item_category_id,
      name,
      short_code,
    },
  });

// ---------- Product Assorting endpoints ----------

export const showProductAssorting = () =>
  erpFetch("/product_assorting", {
    method: "POST",
    body: { type: "show" },
    dedupeInFlight: true,
  });

// ---------- Missing stock (stock check) endpoints ----------

export const verifyMissingStock = ({ branch_id, tag_nos } = {}) =>
  erpFetch("/missing_stock", {
    method: "POST",
    body: {
      type: "verify",
      ...(branch_id != null ? { branch_id } : {}),
      tag_nos: Array.isArray(tag_nos) ? tag_nos : [],
    },
  });

export const createMissingStock = ({
  branch_id,
  scanned_count,
  missing_tag_nos,
  remarks,
} = {}) =>
  erpFetch("/missing_stock", {
    method: "POST",
    body: {
      type: "create",
      ...(branch_id != null ? { branch_id } : {}),
      scanned_count,
      missing_tag_nos: Array.isArray(missing_tag_nos) ? missing_tag_nos : [],
      ...(remarks != null && String(remarks).trim() !== ""
        ? { remarks: String(remarks).trim() }
        : {}),
    },
  });

function resolveErpSessionBranchId() {
  return getActiveBranchId();
}

/**
 * Quote PIN + navbar scope "All" → ledger/rojmel body gets book: "mixed".
 * Quotation scope + access/GST PIN: leave book untouched (do not set).
 * Avoids importing const.js (const imports erpClient → circular).
 */
function applyQuoteMixedBook(body) {
  if (!body || typeof body !== "object" || Array.isArray(body)) return body;
  if (body.book !== undefined && body.book !== null && body.book !== "") {
    return body;
  }
  try {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return body;
    }
    const pin = String(localStorage.getItem("currentPinType") || "access")
      .trim()
      .toLowerCase();
    const scope = String(
      localStorage.getItem("quoteInvoiceListScope") || "quotation",
    )
      .trim()
      .toLowerCase();
    if (pin === "quote" && scope === "all") {
      return { ...body, book: "mixed" };
    }
  } catch {
    // ignore
  }
  return body;
}

// ---------- Unified ledgers: POST /api/ledgers (plain JSON + Bearer) ----------
// Legacy screens used `type: "entire"` for “no date filter”; the unified API uses `filter_type: "entire"`.
export function toUnifiedLedgerRequest(ledgerType, payload = {}) {
  const branch_id =
    payload.branch_id != null && payload.branch_id !== ""
      ? Number(payload.branch_id) || payload.branch_id
      : resolveErpSessionBranchId();
  const useEntire =
    payload.type === "entire" ||
    String(payload.filter_type || "").toLowerCase() === "entire" ||
    String(payload.range_type || "").toLowerCase() === "entire" ||
    String(payload.time_type || "").toLowerCase() === "entire";

  const next = { ...payload };
  delete next.type;
  delete next.branch_id;
  delete next.range_type;
  delete next.time_type;
  if (useEntire) {
    delete next.filter_type;
  }

  const body = {
    type: ledgerType,
    branch_id,
    ...next,
  };
  if (useEntire) {
    body.filter_type = "entire";
  }
  return applyQuoteMixedBook(body);
}

export const postLedgers = (body = {}) =>
  erpFetch("/ledgers", { method: "POST", body: applyQuoteMixedBook(body) });

/** Girvi bank ledger — unified POST /api/ledgers (permission GIRVI_BANK_LEDGER). */
export const postGirviBankLedger = (payload) =>
  postLedgers(toUnifiedLedgerRequest("girvi_bank_ledger", payload));

/** @deprecated Prefer postGirviBankLedger — same unified ledgers endpoint. */
export const listGirviBankLedger = ({
  bid,
  branch_id,
  from_date,
  to_date,
  filter_type,
  page,
  limit,
} = {}) =>
  postGirviBankLedger({
    bid,
    branch_id,
    from_date,
    to_date,
    filter_type,
    page,
    limit,
  });

export const postUserLedger = (payload) =>
  postLedgers(toUnifiedLedgerRequest("user_ledger", payload));
export const postBankAccountLedger = (payload) =>
  postLedgers(toUnifiedLedgerRequest("bank_account_ledger", payload));
export const postKarigarLedger = (payload) =>
  postLedgers(toUnifiedLedgerRequest("karigar_ledger", payload));
export const postOverallLedger = (payload) =>
  postLedgers(toUnifiedLedgerRequest("overall_ledger", payload));
export const postPurchaseLedger = (payload) =>
  postLedgers(toUnifiedLedgerRequest("purchase_ledger", payload));
export const postSalesLedger = (payload) =>
  postLedgers(toUnifiedLedgerRequest("sales_ledger", payload));
export const postUrdLedger = (payload) =>
  postLedgers(toUnifiedLedgerRequest("urd_ledger", payload));

// ---------- Legacy-compatible POST routes (plain JSON + Bearer, same path as modular API) ----------
const postErpRoute = (
  routeKey,
  body = {},
  { idempotencyKey, skipFinancialYear, pinTypeOverride } = {},
) =>
  erpFetch(`/${String(routeKey).replace(/^\//, "")}`, {
    method: "POST",
    body,
    idempotencyKey,
    skipFinancialYear,
    ...(pinTypeOverride != null ? { pinTypeOverride } : null),
  });

export const postShowUsersAdmin = (body) =>
  postErpRoute("show_users_admin", body);
export const postCustomerSettlement = (body) =>
  postErpRoute("customer_settlement", body);
export const postKarigarConversion = (body, { idempotencyKey } = {}) =>
  postErpRoute("karigar_conversion", body, { idempotencyKey });
export const postSupplierConversion = (body) =>
  postErpRoute("supplier_conversion", body);
export const postSalesReturn = (body, { idempotencyKey } = {}) =>
  erpFetch("/sale_return", {
    method: "POST",
    body,
    idempotencyKey,
  });

export const postMetalTransfer = (body, { idempotencyKey } = {}) =>
  erpFetch("/metal_transfer", {
    method: "POST",
    body,
    idempotencyKey,
  });

/** Break tagged product back into untagged product_group_pool. */
export const cancelProductTag = (payload = {}, { idempotencyKey } = {}) =>
  erpFetch("/product", {
    method: "POST",
    body: {
      type: "cancel_tag",
      ...payload,
    },
    idempotencyKey,
  });

export const postPaymentSettlement = (body) =>
  postErpRoute("payment_settlement", body);
export const postCancelOrder = (body, { idempotencyKey } = {}) =>
  postErpRoute("cancel_order", body, { idempotencyKey });
export const postDashboard = (body) => postErpRoute("dashboard", body);
export const postEmployeePermission = (body) =>
  postErpRoute("employee_permission", body);
export const postReadyProducts = (body) => postErpRoute("ready_products", body);
export const postSaleNotCompleted = (body) =>
  postErpRoute("sale_not_completed", body);
export const postSelfCustomerForm = (body) =>
  postErpRoute("self_customer_form", body);
export const postCustomerForm = (body) => postErpRoute("customer_form", body);

/** Public kiosk walk-in — uses REACT_APP_KIOSK_TOKEN when configured. */
export const postCustomerFormKiosk = (body) =>
  erpFetch("/customer_form", {
    method: "POST",
    body,
    tokenOverride: getKioskErpToken(),
  });
export const postSignup = (body) =>
  postErpRoute("signup", body, { skipFinancialYear: true });
export const postGirviPaymentEntry = (body) =>
  postErpRoute("girvi_payment_entry", body);
export const postGirviDashboard = (body) =>
  erpFetch("/girvi_dashboard", {
    method: "POST",
    body,
    dedupeInFlight: true,
  });
/**
 * Cashbook show/list. Mixed book: pass pinTypeOverride from bill_type
 * (Quotation → "quote", GST → "access").
 */
export const postCashbook = (
  body,
  { idempotencyKey, pinTypeOverride } = {},
) => postErpRoute("cashbook", body, { idempotencyKey, pinTypeOverride });

/**
 * Rojmel (Day Book) — POST /api/rojmel (quote → /quote/rojmel).
 * Body: { type: day_work|amount|gold_fine|silver_fine|today_total, date: YYYY-MM-DD, branch_id }
 * Quote PIN + scope All → book: "mixed".
 * dedupeInFlight: avoids Strict Mode / double-effect duplicate POSTs.
 */
export const postRojmel = (payload = {}) => {
  const branch_id =
    payload.branch_id != null && payload.branch_id !== ""
      ? Number(payload.branch_id) || payload.branch_id
      : resolveErpSessionBranchId();
  return erpFetch("/rojmel", {
    method: "POST",
    body: applyQuoteMixedBook({
      type: payload.type || "day_work",
      date: payload.date,
      ...(branch_id != null ? { branch_id } : null),
      ...(payload.book != null && payload.book !== ""
        ? { book: payload.book }
        : null),
    }),
    dedupeInFlight: true,
  });
};

/**
 * Shop / branch operating expenses.
 * POST /api/shop_expenses — RBAC: SHOP_EXPENSE_TABLE, ADD_SHOP_EXPENSE, EDIT_SHOP_EXPENSE.
 * Create requires Idempotency-Key header (see createShopExpense).
 */
export const listShopExpenses = ({
  expense_type,
  from_date,
  to_date,
  pagination,
} = {}) => {
  const body = { type: "list" };
  if (expense_type != null && String(expense_type).trim() !== "") {
    body.expense_type = String(expense_type).trim().toUpperCase();
  }
  if (from_date) body.from_date = from_date;
  if (to_date) body.to_date = to_date;
  if (pagination && typeof pagination === "object") {
    body.pagination = pagination;
  }
  return erpFetch("/shop_expenses", {
    method: "POST",
    body,
    dedupeInFlight: true,
  });
};

export const showShopExpense = ({ id } = {}) =>
  erpFetch("/shop_expenses", {
    method: "POST",
    body: { type: "show", id: Number(id) },
  });

/** True when shop expense uses a bank account (payment_mode BANK). */
function isShopExpenseBankPaymentMode(payment_mode) {
  return (
    String(payment_mode || "")
      .trim()
      .toUpperCase() === "BANK"
  );
}

export const createShopExpense = (payload = {}, { idempotencyKey } = {}) => {
  if (idempotencyKey == null || String(idempotencyKey).trim() === "") {
    throw new Error("createShopExpense requires a non-empty idempotencyKey");
  }
  const {
    expense_type,
    amount,
    expense_date,
    comment,
    idempotency_key,
    payment_mode,
    pay_method,
    bank_account_id,
    branch_id,
  } = payload;
  const body = {
    type: "create",
    expense_type: String(expense_type || "")
      .trim()
      .toUpperCase(),
    amount: Number(amount),
    expense_date: String(expense_date || "").trim(),
    comment:
      comment == null || String(comment).trim() === ""
        ? null
        : String(comment).trim(),
    payment_mode: String(payment_mode || "BANK")
      .trim()
      .toUpperCase(),
  };
  if (isShopExpenseBankPaymentMode(body.payment_mode)) {
    if (pay_method != null && String(pay_method).trim() !== "") {
      body.pay_method = String(pay_method).trim().toUpperCase();
    }
    if (bank_account_id != null) {
      body.bank_account_id = Number(bank_account_id);
    }
  }
  if (branch_id != null && Number.isFinite(Number(branch_id))) {
    body.branch_id = Number(branch_id);
  }
  if (idempotency_key) {
    body.idempotency_key = String(idempotency_key);
  }
  return erpFetch("/shop_expenses", {
    method: "POST",
    body,
    idempotencyKey,
  });
};

export const updateShopExpense = ({
  id,
  expense_type,
  amount,
  expense_date,
  comment,
  payment_mode,
  pay_method,
  bank_account_id,
  branch_id,
} = {}) => {
  const body = {
    type: "update",
    id: Number(id),
    expense_type: String(expense_type || "")
      .trim()
      .toUpperCase(),
    amount: Number(amount),
    expense_date: String(expense_date || "").trim(),
    payment_mode: String(payment_mode || "BANK")
      .trim()
      .toUpperCase(),
  };
  if (isShopExpenseBankPaymentMode(body.payment_mode)) {
    if (pay_method != null && String(pay_method).trim() !== "") {
      body.pay_method = String(pay_method).trim().toUpperCase();
    }
    if (bank_account_id != null) {
      body.bank_account_id = Number(bank_account_id);
    }
  }
  if (branch_id != null && Number.isFinite(Number(branch_id))) {
    body.branch_id = Number(branch_id);
  }
  if (comment != null && String(comment).trim() !== "") {
    body.comment = String(comment).trim();
  } else {
    body.comment = null;
  }
  return erpFetch("/shop_expenses", { method: "POST", body });
};

export const deleteShopExpense = ({ id } = {}) =>
  erpFetch("/shop_expenses", {
    method: "POST",
    body: { type: "delete", id: Number(id) },
  });

/** POST /api/sales_targets/ — Part A in `sales_targets_hrms_api.md` */
export const listSalesTargets = ({ branch_id, month } = {}) =>
  erpFetch("/sales_targets/", {
    method: "POST",
    body: {
      type: "list",
      branch_id: Number(branch_id),
      month: String(month || "").trim(),
    },
    dedupeInFlight: true,
  });

export const saveSalesTargets = ({
  branch_id,
  month,
  shop_target_amount,
  note,
  employee_targets,
} = {}) => {
  const body = {
    type: "save",
    branch_id: Number(branch_id),
    month: String(month || "").trim(),
  };
  if (shop_target_amount != null && shop_target_amount !== "") {
    const n = Number(shop_target_amount);
    if (Number.isFinite(n)) {
      body.shop_target_amount = n;
      body.note = note == null || note === undefined ? "" : String(note).trim();
    }
  }
  if (Array.isArray(employee_targets)) {
    body.employee_targets = employee_targets.map((r) => ({
      user_admin_id: Number(r.user_admin_id),
      target_amount: Number(r.target_amount),
      note: r.note == null || r.note === undefined ? "" : String(r.note).trim(),
    }));
  }
  return erpFetch("/sales_targets/", { method: "POST", body });
};

export const deleteSalesTarget = ({
  branch_id,
  month,
  scope,
  user_admin_id,
} = {}) => {
  const body = {
    type: "delete",
    branch_id: Number(branch_id),
    month: String(month || "").trim(),
    scope: String(scope || "").toUpperCase(),
  };
  if (body.scope === "EMPLOYEE" && user_admin_id != null) {
    body.user_admin_id = Number(user_admin_id);
  }
  return erpFetch("/sales_targets/", { method: "POST", body });
};

/** POST /api/hrms/ — Part B in `sales_targets_hrms_api.md` (RBAC per `type`). */
export const postHrms = (body = {}) =>
  erpFetch("/hrms/", {
    method: "POST",
    body: typeof body === "object" && body !== null ? body : {},
    skipFinancialYear: true,
  });

/** Multipart POST /api/hrms/ (e.g. `holiday_import_excel`). */
export const postHrmsMultipart = (formData) =>
  postMultipartToRoute("/hrms/", formData);

export const updateGirviBank = (fields = {}) => {
  clearGirviBanksListCache();
  return erpFetch("/girvi_bank", {
    method: "POST",
    body: { type: "update", ...fields },
  });
};

const postMultipartToRoute = (path, formData) => {
  const baseUrl = getErpBaseUrl();
  const url = `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
  const token = getErpToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const pathNorm = String(path || "").toLowerCase();
  const skipBranchHeader =
    pathNorm.includes("whatsapp") ||
    pathNorm.includes("daily_price") ||
    pathNorm.includes("daily-price");
  if (token && !skipBranchHeader) {
    const activeBranchId = getActiveBranchId();
    if (activeBranchId != null) {
      headers["X-Branch-Id"] = String(activeBranchId);
    }
  }
  const financialYear = getFinancialYear();
  if (
    financialYear &&
    formData instanceof FormData &&
    !formData.has("financial_year")
  ) {
    formData.append("financial_year", financialYear);
  }
  return axiosErp.post(url, formData, { headers }).then((r) => r.data);
};

/** Clone FormData and prepend `type` for POST /custom_orders */
export const createCustomOrderMultipartForm = (formData) => {
  const fd = new FormData();
  fd.append("type", "create");
  for (const [key, value] of formData.entries()) {
    fd.append(key, value);
  }
  return postMultipartToRoute("/custom_orders", fd);
};

export const updateCustomOrderMultipartForm = (formData) => {
  const fd = new FormData();
  fd.append("type", "update");
  for (const [key, value] of formData.entries()) {
    fd.append(key, value);
  }
  // Match what postMultipartToRoute appends so the log matches the wire request.
  const financialYear = getFinancialYear();
  if (financialYear && !fd.has("financial_year")) {
    fd.append("financial_year", financialYear);
  }

  const fullPayload = {};
  for (const [key, val] of fd.entries()) {
    if (key === "items" || key === "payments") {
      try {
        fullPayload[key] = JSON.parse(val);
      } catch {
        fullPayload[key] = val;
      }
    } else if (val instanceof File) {
      fullPayload[key] = {
        name: val.name,
        size: val.size,
        type: val.type,
      };
    } else {
      fullPayload[key] = val;
    }
  }
  console.log(
    "[custom_orders type=update] FULL REQUEST PAYLOAD:",
    fullPayload,
  );
  console.log(
    "[custom_orders type=update] FULL REQUEST JSON:\n",
    JSON.stringify(fullPayload, null, 2),
  );

  return postMultipartToRoute("/custom_orders", fd);
};

export const postEditUserProfile = (formData) =>
  postMultipartToRoute("/edit_user", formData);

// ---------- Leads (POST /api/leads) ----------
export const postLeads = (payload = {}) => {
  const { branch_id, ...rest } = payload;
  return erpFetch("/leads", {
    method: "POST",
    body: {
      branch_id: branch_id != null ? branch_id : 1,
      ...rest,
    },
    dedupeInFlight: true,
  });
};

export const listLeads = ({
  branch_id,
  status,
  stage,
  lead_type,
  assigned_to,
  search,
  pagination,
  page,
  limit,
} = {}) => {
  const body = { type: "list" };
  if (branch_id != null) body.branch_id = branch_id;
  if (status) body.status = status;
  if (stage) body.stage = stage;
  if (lead_type) body.lead_type = lead_type;
  if (assigned_to != null) body.assigned_to = assigned_to;
  if (search != null && search !== "") body.search = String(search).trim();
  if (pagination && typeof pagination === "object")
    body.pagination = pagination;
  if (page != null) body.page = page;
  if (limit != null) body.limit = limit;
  return postLeads(body);
};

export const showLead = ({ id, lead_id, branch_id } = {}) => {
  const lid = id ?? lead_id;
  return postLeads({
    type: "show",
    ...(lid != null ? { id: Number(lid) } : {}),
    ...(branch_id != null ? { branch_id } : {}),
  });
};

export const createLead = (payload = {}) =>
  postLeads({ type: "create", ...payload });

export const updateLead = (payload = {}) =>
  postLeads({ type: "update", ...payload });

export const changeLeadStage = (payload = {}) =>
  postLeads({ type: "change_stage", ...payload });

export const addLeadActivity = (payload = {}) =>
  postLeads({ type: "add_activity", ...payload });

export const closeLead = (payload = {}) =>
  postLeads({ type: "close", ...payload });

export const reopenLead = (payload = {}) =>
  postLeads({ type: "reopen", ...payload });

export const dueFollowupsLeads = ({ branch_id } = {}) =>
  postLeads({
    type: "due_followups",
    ...(branch_id != null ? { branch_id } : {}),
  });

export const nextLeadFollowup = (payload = {}) =>
  postLeads({ type: "next_followup", ...payload });

export const partialPaymentLead = (payload = {}) =>
  postLeads({ type: "partial_payment", ...payload });

// ---------- Reminders (POST /api/reminders) ----------
export const postReminders = (payload = {}) => {
  const { branch_id, ...rest } = payload;
  return erpFetch("/reminders", {
    method: "POST",
    body: {
      branch_id: branch_id != null ? branch_id : 1,
      ...rest,
    },
    dedupeInFlight: true,
  });
};

/** Fallback if backend mounts next_followup on /reminders. */
export const nextReminderFollowup = (payload = {}) =>
  postReminders({ type: "next_followup", ...payload });

/** Fallback if backend mounts partial_payment on /reminders. */
export const partialPaymentReminder = (payload = {}) =>
  postReminders({ type: "partial_payment", ...payload });

export const listPendingReminders = ({
  branch_id,
  limit,
  filter,
  for_date,
} = {}) => {
  const body = { type: "list_pending" };
  if (branch_id != null) body.branch_id = branch_id;
  if (limit != null) body.limit = limit;
  if (filter) body.filter = filter;
  if (for_date) body.for_date = for_date;
  return postReminders(body);
};

export const sendRemindersNow = ({ branch_id, max_batch } = {}) =>
  postReminders({
    type: "send_now",
    ...(branch_id != null ? { branch_id } : {}),
    ...(max_batch != null ? { max_batch } : {}),
  });

/** Reschedule lead follow-up via reminders snooze (lead_id + scheduled_at). */
export const snoozeReminder = (payload = {}) =>
  postReminders({ type: "snooze", ...payload });

export const markReminderSkipped = (payload = {}) =>
  postReminders({ type: "mark_skipped", ...payload });

export const closeReminderLead = (payload = {}) =>
  postReminders({ type: "close_lead", ...payload });

export const runRemindersScheduler = ({ branch_id } = {}) =>
  postReminders({
    type: "run_scheduler",
    ...(branch_id != null ? { branch_id } : {}),
  });

export const remindersDashboard = ({ branch_id } = {}) =>
  postReminders({
    type: "dashboard",
    ...(branch_id != null ? { branch_id } : {}),
  });

export const enqueueRemindersForLead = ({
  lead_id,
  leadId,
  branch_id,
} = {}) => {
  const lid = lead_id ?? leadId;
  return postReminders({
    type: "enqueue",
    ...(lid != null ? { lead_id: Number(lid) } : {}),
    ...(branch_id != null ? { branch_id } : {}),
  });
};

// Simple permission guard helper (ERP 1/0 flags)
export const hasErpPermission = (code) => {
  const perms = getErpPermissions();
  return perms && perms[code] === 1;
};

export const withErpPermission = (code, Component) => (props) => {
  if (!hasErpPermission(code)) {
    // Caller should handle redirect; here we just render nothing/placeholder
    return null;
  }
  return <Component {...props} />;
};
